import * as THREE from 'three';
import { createMobMesh } from './mobs/mesh-factory.js';
import { HOSTILE_SPAWN_WEIGHTS, PASSIVE_SPAWN_WEIGHTS, MAX_HOSTILE_MOBS, HOSTILE_SPAWN_BASE_INTERVAL, HOSTILE_SPAWN_NIGHT_ACCEL, MOB_CONFIG, pickWeighted } from './mobs/config.js';
import {
  NETHER_HOSTILE_SPAWN_WEIGHTS, END_HOSTILE_SPAWN_WEIGHTS,
  OUTPOST_HOSTILE_SPAWN_WEIGHTS, MONSTER_DEN_HOSTILE_SPAWN_WEIGHTS,
  RUIN_HOSTILE_SPAWN_WEIGHTS, SHIPWRECK_HOSTILE_SPAWN_WEIGHTS,
  HAMLET_PASSIVE_SPAWN_WEIGHTS, TRIBE_PASSIVE_SPAWN_WEIGHTS,
  WETLAND_PASSIVE_SPAWN_WEIGHTS, MOUNTAIN_PASSIVE_SPAWN_WEIGHTS,
  BREEDABLE_PASSIVE_TYPES, PASSIVE_BREED_FOOD
} from './mobs/spawn-weights.js';

const arrowGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.62, 6);
arrowGeo.rotateX(Math.PI / 2);

const MAX_NETHER_HOSTILES = 10;
const MAX_END_HOSTILES = 7;
const NETHER_HOSTILE_SPAWN_INTERVAL = 3.1;
const END_HOSTILE_SPAWN_INTERVAL = 4.0;
const ENDERMAN_UNLOCK_DAY = 12;
const ENDERMAN_FULL_WEIGHT_DAY = 20;
const NEWBIE_WORLD_SPAWN_SAFE_RADIUS = 56;
const NEWBIE_WORLD_SPAWN_SAFE_RADIUS_SQ = NEWBIE_WORLD_SPAWN_SAFE_RADIUS * NEWBIE_WORLD_SPAWN_SAFE_RADIUS;
const NEWBIE_WORLD_SPAWN_GRACE_DAYS = 2;
const MOB_HP_SHOW_DISTANCE = 18;
const MOB_SOFT_DESPAWN_DISTANCE = 60;
const MOB_HARD_DESPAWN_DISTANCE = 92;
const MOB_SPAWN_GRACE_SECONDS = 10;
const MOB_HEAVY_LOAD_COUNT = 18;
const MOB_CRITICAL_LOAD_COUNT = 26;
const MOB_VISUAL_ALWAYS_DISTANCE = 16;
const MOB_VISUAL_RENDER_DISTANCE = 52;
const MOB_VISUAL_RETAIN_SECONDS = 0.2;
const MOB_VISUAL_HYSTERESIS_DOT = 0.18;
const MOB_AI_ACCUM_MAX = 0.22;
const MOB_AI_NEAR_DIST = 18;
const MOB_AI_MID_DIST = 34;
const MOB_AI_FAR_DIST = 56;
const BABY_MOB_SCALE = 0.62;
const PASSIVE_LOVE_DURATION = 18;
const PASSIVE_BREED_COOLDOWN = 50;
const PASSIVE_BABY_GROW_DURATION = 170;
const PASSIVE_FEED_HEAL = 2;
const PASSIVE_BREED_RANGE = 1.85;
const PASSIVE_BREED_FOLLOW_RANGE = 14;
const LAND_WATER_ESCAPE_RADII = [1.1, 1.8, 2.6, 3.4];
const LAND_WATER_ESCAPE_DIRS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [0.7071, 0.7071],
  [0.7071, -0.7071],
  [-0.7071, 0.7071],
  [-0.7071, -0.7071]
];

export function createMobSystem({
  scene,
  camera = null,
  world,
  player,
  onDamagePlayer,
  onCreeperExplode,
  onMobSound = null,
  onMobDeath = null,
  onMobDrop = null,
  onMobDamaged = null,
  t: i18nTranslate = null
}) {
  const mobs = [];
  const arrows = [];
  const deadMobs = [];
  const tempVec = new THREE.Vector3();
  const playerHead = new THREE.Vector3();
  const raycaster = new THREE.Raycaster();
  raycaster.layers.set(1);
  const hitboxToMob = new Map();
  const spawnPos = new THREE.Vector3();
  const losProbe = new THREE.Vector3();
  const losDir = new THREE.Vector3();
  const playerBlastSample = new THREE.Vector3();
  const playerBlastCenter = new THREE.Vector3();
  const mobViewDir = new THREE.Vector3();
  const mobAttackOrigin = new THREE.Vector3();
  const arrowLookTarget = new THREE.Vector3();
  const rangedShotOrigin = new THREE.Vector3();
  const ecosystemQueryCache = new Map();
  const lightQueryCache = new Map();
  const MOB_RADIUS = 0.34;
  const MOB_HEIGHT = 1.9;
  const MOB_STEP_UP_BLOCKS = 1;
  const MOB_MAX_DROP_BLOCKS = 2;
  const MOB_MOVE_STEP = 0.08;
  const LOS_STEP = 0.18;
  const MOB_LAVA_DAMAGE_INTERVAL = 0.6;
  const MOB_LAVA_DAMAGE = 3;
  const VILLAGER_NEARBY_BLOCK_IDS = new Set([12, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 29, 33, 34, 39]);
  const HOSTILE_LIGHT_BLOCK_IDS = new Set([12, 38, 43, 46]);
  const END_CENTER_Z = 0;
  const DRAGON_MIN_ALTITUDE = 12;
  const DRAGON_ORBIT_Y_SWAY = 2.8;
  const DRAGON_SWOOP_TRIGGER_DIST = 42;
  const DRAGON_SWOOP_DURATION = 2.5;
  const MOB_NAME_SHOW_DISTANCE = 14.5;
  const VILLAGER_PROFESSIONS = ['farmer', 'fletcher', 'blacksmith', 'armorer', 'cleric', 'butcher'];
  const VILLAGER_PROFESSION_LABELS = {
    farmer: 'Farmer',
    fletcher: 'Fletcher',
    blacksmith: 'Blacksmith',
    armorer: 'Armorer',
    cleric: 'Cleric',
    butcher: 'Butcher'
  };
  const MOB_DISPLAY_NAMES = {
    zombie: 'Zombie',
    skeleton: 'Skeleton',
    skeleton_archer: 'Skeleton Archer',
    creeper: 'Creeper',
    spider: 'Spider',
    enderman: 'Enderman',
    husk: 'Husk',
    stray: 'Stray',
    pillager: 'Pillager',
    raider_brute: 'Raider Brute',
    drowned: 'Drowned',
    ender_dragon: 'Ender Dragon',
    villager: 'Villager',
    cow: 'Cow',
    sheep: 'Sheep',
    pig: 'Pig',
    chicken: 'Chicken',
    goat: 'Goat',
    wild_boar: 'Wild Boar',
    duck: 'Duck'
  };
  const mobNameTextureCache = new Map();
  const solidForCollisionAt = typeof world.isSolidForCollisionAt === 'function'
    ? (x, y, z) => world.isSolidForCollisionAt(x, y, z)
    : (x, y, z) => world.isSolidAt(x, y, z);
  let hostileSpawnTimer = 0;
  let passiveSpawnTimer = 0;
  let mobSeq = 1;
  let mobUpdateFrame = 0;
  let explosionFx = [];
  let endDragonDefeated = false;
  let creativeModeActive = false;
  let runtimePerformanceTier = 1;
  let runtimeTerrainProbeBudget = 42;
  let runtimeHighPrecisionMoveBudget = 22;
  let runtimeLosRefreshBudget = 24;
  let runtimeLosRefreshBudgetInitial = 24;
  let runtimeLosRefreshUsed = 0;
  let runtimeLosRefreshSkipped = 0;

  const dragonTarget = new THREE.Vector3();
  const dragonMoveDir = new THREE.Vector3();
  const dragonSpawnPos = new THREE.Vector3();
  const breedSearchVec = new THREE.Vector3();
  const feedPos = new THREE.Vector3();
  const waterEscapeDir = new THREE.Vector2();

  function isBreedablePassiveType(type) {
    return BREEDABLE_PASSIVE_TYPES.has(type);
  }

  function canFeedMobType(type, itemId) {
    if (!isBreedablePassiveType(type)) return false;
    const key = String(itemId || '');
    if (!key) return false;
    return PASSIVE_BREED_FOOD[type]?.has(key) === true;
  }

  function getAmbientCooldown(type, behavior) {
    if (type === 'ender_dragon') return 4.0 + Math.random() * 2.8;
    if (type === 'enderman') return 4.8 + Math.random() * 3.8;
    if (type === 'creeper') return 5.6 + Math.random() * 4.2;
    if (behavior === 'hostile') return 3.8 + Math.random() * 3.8;
    return 6.2 + Math.random() * 5.2;
  }

  function emitMobSound(mob, event, extra = {}) {
    if (typeof onMobSound !== 'function' || !mob) return;
    onMobSound({
      event,
      type: mob.type,
      position: mob.mesh?.position ? mob.mesh.position.clone() : null,
      ...extra
    });
  }

  function toTitleCaseWords(text) {
    return String(text || '')
      .split('_')
      .filter(Boolean)
      .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
      .join(' ');
  }

  function tr(key, vars = {}, fallback = key) {
    if (typeof i18nTranslate !== 'function') return fallback;
    const localized = i18nTranslate(key, vars);
    if (localized === key) return fallback;
    return localized;
  }

  function getVillagerProfessionName(profession) {
    const key = String(profession || 'farmer');
    return tr(`villager.profession.${key}`, {}, VILLAGER_PROFESSION_LABELS[key] || toTitleCaseWords(key));
  }

  function getMobTypeDisplayName(type) {
    const key = String(type || '');
    return tr(`mob.${key}`, {}, MOB_DISPLAY_NAMES[key] || toTitleCaseWords(key) || 'Mob');
  }

  function getMobDisplayName(type, mob = null) {
    if (type === 'villager' && mob?.profession) {
      const professionName = getVillagerProfessionName(mob.profession);
      return tr(
        'mob.villager_with_profession',
        { profession: professionName },
        `${professionName} ${getMobTypeDisplayName('villager')}`
      );
    }
    return getMobTypeDisplayName(type);
  }

  function getMobNameTexture(name) {
    const key = String(name || 'Mob');
    const cached = mobNameTextureCache.get(key);
    if (cached) return cached;

    const canvas = document.createElement('canvas');
    const baseHeight = 64;
    canvas.width = 256;
    canvas.height = baseHeight;
    const measureCtx = canvas.getContext('2d');
    measureCtx.font = '700 24px "Trebuchet MS", "Segoe UI", sans-serif';
    const measuredWidth = Math.ceil(measureCtx.measureText(key).width);
    const totalWidth = Math.max(96, measuredWidth + 38);
    canvas.width = totalWidth;
    canvas.height = baseHeight;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(10, 10, 10, 0.62)';
    ctx.fillRect(0, 11, canvas.width, 42);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 12, canvas.width - 2, 40);
    ctx.font = '700 24px "Trebuchet MS", "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.97)';
    ctx.fillText(key, canvas.width * 0.5, canvas.height * 0.5);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    const entry = {
      texture,
      aspect: canvas.width / canvas.height
    };
    mobNameTextureCache.set(key, entry);
    return entry;
  }

  function createMobNameTag(name) {
    const textureEntry = getMobNameTexture(name);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: textureEntry.texture,
      transparent: true,
      depthWrite: false,
      toneMapped: false
    }));
    const baseHeight = 0.34;
    sprite.scale.set(baseHeight * textureEntry.aspect, baseHeight, 1);
    sprite.position.set(0, 0.26, 0.03);
    sprite.renderOrder = 4;
    sprite.visible = false;
    return sprite;
  }

  function disposeMobNameTag(tag) {
    if (!tag?.material) return;
    tag.material.dispose?.();
  }

  function refreshMobDisplayName(mob) {
    if (!mob?.hpRoot) return;
    mob.name = getMobDisplayName(mob.type, mob);

    if (mob.nameTag) {
      mob.hpRoot.remove(mob.nameTag);
      disposeMobNameTag(mob.nameTag);
    }

    mob.nameTag = createMobNameTag(mob.name);
    mob.hpRoot.add(mob.nameTag);
  }

  function worldToBlock(v) {
    return Math.floor(v + 0.5);
  }

  function getDimensionAtPos(pos) {
    return world.getDimensionAt(pos.x, pos.z);
  }

  function isMobInLava(mob) {
    if (!mob || typeof world.isLavaAt !== 'function') return false;
    const pos = mob.mesh?.position;
    if (!pos) return false;
    const samples = [
      [pos.x, pos.y + 0.2, pos.z],
      [pos.x, pos.y + 0.85, pos.z],
      [pos.x - MOB_RADIUS * 0.7, pos.y + 0.48, pos.z - MOB_RADIUS * 0.7],
      [pos.x + MOB_RADIUS * 0.7, pos.y + 0.48, pos.z - MOB_RADIUS * 0.7],
      [pos.x - MOB_RADIUS * 0.7, pos.y + 0.48, pos.z + MOB_RADIUS * 0.7],
      [pos.x + MOB_RADIUS * 0.7, pos.y + 0.48, pos.z + MOB_RADIUS * 0.7]
    ];

    for (const [sx, sy, sz] of samples) {
      if (world.isLavaAt(worldToBlock(sx), worldToBlock(sy), worldToBlock(sz))) return true;
    }
    return false;
  }

  function isMobInWater(mob) {
    if (!mob || typeof world.isWaterAt !== 'function') return false;
    const pos = mob.mesh?.position;
    if (!pos) return false;
    const edge = MOB_RADIUS * 0.7;
    const samples = [
      [pos.x, pos.y + 0.2, pos.z],
      [pos.x, pos.y + 0.85, pos.z],
      [pos.x - edge, pos.y + 0.48, pos.z - edge],
      [pos.x + edge, pos.y + 0.48, pos.z - edge],
      [pos.x - edge, pos.y + 0.48, pos.z + edge],
      [pos.x + edge, pos.y + 0.48, pos.z + edge]
    ];

    for (const [sx, sy, sz] of samples) {
      if (world.isWaterAt(worldToBlock(sx), worldToBlock(sy), worldToBlock(sz))) return true;
    }
    return false;
  }

  function mobCollidesAt(x, y, z) {
    const minX = worldToBlock(x - MOB_RADIUS);
    const maxX = worldToBlock(x + MOB_RADIUS);
    const minY = worldToBlock(y + 0.02);
    const maxY = worldToBlock(y + MOB_HEIGHT - 0.02);
    const minZ = worldToBlock(z - MOB_RADIUS);
    const maxZ = worldToBlock(z + MOB_RADIUS);

    for (let bx = minX; bx <= maxX; bx++) {
      for (let by = minY; by <= maxY; by++) {
        for (let bz = minZ; bz <= maxZ; bz++) {
          if (solidForCollisionAt(bx, by, bz)) return true;
        }
      }
    }

    return false;
  }

  function getGroundSupportState(x, supportY, z) {
    const r = MOB_RADIUS - 0.03;
    const samples = [
      [x, z],
      [x - r, z - r],
      [x + r, z - r],
      [x - r, z + r],
      [x + r, z + r]
    ];

    let count = 0;
    let center = false;
    for (const [sx, sz] of samples) {
      const bx = worldToBlock(sx);
      const bz = worldToBlock(sz);
      if (!solidForCollisionAt(bx, supportY, bz)) continue;
      count += 1;
      if (sx === x && sz === z) center = true;
    }

    return { count, center };
  }

  function mobHasGroundSupportAt(x, supportY, z, opts = {}) {
    const requireCenter = opts.requireCenter !== false;
    const minCount = Math.max(1, Math.floor(opts.minCount || 2));
    const support = getGroundSupportState(x, supportY, z);
    if (support.count < minCount) return false;
    if (requireCenter && !support.center) return false;
    return true;
  }

  function findWalkableMobY(x, z, baseY, avoidWater = false) {
    const bx = worldToBlock(x);
    const bz = worldToBlock(z);
    const baseSupportY = worldToBlock(baseY - 0.08);
    const minSupportY = baseSupportY - MOB_MAX_DROP_BLOCKS;
    const maxSupportY = baseSupportY + MOB_STEP_UP_BLOCKS;

    for (let supportY = maxSupportY; supportY >= minSupportY; supportY--) {
      if (!mobHasGroundSupportAt(x, supportY, z, { requireCenter: true, minCount: 3 })) continue;

      const feetY = supportY + 0.5;
      if (avoidWater && world.isWaterAt(bx, worldToBlock(feetY + 0.25), bz)) continue;
      if (mobCollidesAt(x, feetY, z)) continue;
      return feetY;
    }

    return null;
  }

  function tryResolveMobInsideTerrain(mob, avoidWater = false) {
    if (!mob?.mesh) return false;
    const pos = mob.mesh.position;
    if (!mobCollidesAt(pos.x, pos.y, pos.z)) return true;

    const startY = pos.y;
    for (let step = 1; step <= 64; step++) {
      const probeY = startY + step * 0.25;
      if (!mobCollidesAt(pos.x, probeY, pos.z)) {
        pos.y = probeY;
        return true;
      }
    }

    return false;
  }

  function tryTeleportMobNear(mob, centerPos, minDist = 4, maxDist = 12, avoidWater = true, attempts = 18) {
    if (!mob || !centerPos) return false;
    const baseY = mob.mesh.position.y;

    for (let i = 0; i < attempts; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = minDist + Math.random() * Math.max(0.1, (maxDist - minDist));
      const x = centerPos.x + Math.cos(angle) * radius;
      const z = centerPos.z + Math.sin(angle) * radius;
      if (world.getDimensionAt(x, z) !== mob.dimension) continue;

      const y = findWalkableMobY(x, z, baseY, avoidWater);
      if (y === null) continue;

      const bx = worldToBlock(x);
      const by = worldToBlock(y + 0.25);
      const bz = worldToBlock(z);
      if (avoidWater && world.isWaterAt(bx, by, bz)) continue;

      mob.mesh.position.set(x, y, z);
      return true;
    }

    return false;
  }

  function isMobInLiquid(mob) {
    return isMobInWater(mob) || isMobInLava(mob);
  }

  function findLandEscapeDirectionFromWater(mob, outDir) {
    if (!mob?.mesh || !outDir) return false;
    const pos = mob.mesh.position;

    let bestScore = -Infinity;
    let bestDx = 0;
    let bestDz = 0;

    for (const radius of LAND_WATER_ESCAPE_RADII) {
      for (const [dirX, dirZ] of LAND_WATER_ESCAPE_DIRS) {
        const tx = pos.x + dirX * radius;
        const tz = pos.z + dirZ * radius;
        if (world.getDimensionAt(tx, tz) !== mob.dimension) continue;

        const ty = findWalkableMobY(tx, tz, pos.y, true);
        if (ty === null) continue;

        const bx = worldToBlock(tx);
        const by = worldToBlock(ty + 0.25);
        const bz = worldToBlock(tz);
        if (world.isWaterAt(bx, by, bz)) continue;

        const verticalGap = Math.abs(ty - pos.y);
        const score = 12 - radius * 2.2 - verticalGap * 3.4;
        if (score <= bestScore) continue;

        bestScore = score;
        bestDx = tx - pos.x;
        bestDz = tz - pos.z;
      }
    }

    if (!Number.isFinite(bestScore)) return false;
    const len = Math.hypot(bestDx, bestDz);
    if (len <= 0.0001) return false;

    outDir.set(bestDx / len, bestDz / len);
    return true;
  }

  function moveMobWithCollision(mob, dx, dz, delta, yLerpSpeed = 8, avoidWater = false, playerFlatDist = Infinity) {
    const pos = mob.mesh.position;
    const startX = pos.x;
    const startY = pos.y;
    const startZ = pos.z;
    if (!mob.velocity) mob.velocity = new THREE.Vector3(0, 0, 0);
    const highPrecisionRequested = mob.cfg?.behavior === 'boss'
      || playerFlatDist <= (MOB_AI_NEAR_DIST + 2)
      || (mob.type === 'creeper' && mob.fuse > 0.02)
      || mob.hurtFlash > 0.01;
    const highPrecision = highPrecisionRequested
      && (mob.cfg?.behavior === 'boss' || runtimeHighPrecisionMoveBudget > 0);
    if (highPrecision && mob.cfg?.behavior !== 'boss') {
      runtimeHighPrecisionMoveBudget = Math.max(0, runtimeHighPrecisionMoveBudget - 1);
    }
    const underPressure = runtimePerformanceTier <= 0 || delta > 0.03;
    const keepDualStep = highPrecision && (!underPressure || playerFlatDist <= (MOB_AI_NEAR_DIST + 1.5));
    const stepHeights = keepDualStep ? [0.55, 1.05] : [0.55];

    // Apply Gravity and Swimming
    if (isMobInLiquid(mob)) {
      mob.velocity.y += 12.0 * delta;
      if (mob.velocity.y > 3.0) mob.velocity.y = 3.0;
    } else {
      mob.velocity.y -= 25.0 * delta;
      if (mob.velocity.y < -25.0) mob.velocity.y = -25.0;
    }

    // Y collision (Gravity)
    const yStepTotal = mob.velocity.y * delta;
    const yStepSpan = underPressure
      ? (highPrecision ? 0.74 : 1.05)
      : (highPrecision ? 0.58 : 0.95);
    const yMaxSubSteps = underPressure
      ? (highPrecision ? 3 : 2)
      : (highPrecision ? 4 : 3);
    const ySubSteps = Math.max(
      1,
      Math.min(yMaxSubSteps, Math.ceil(Math.abs(yStepTotal) / yStepSpan))
    );
    let hitY = false;

    const dy = yStepTotal / ySubSteps;
    for (let i = 0; i < ySubSteps; i++) {
      pos.y += dy;
      if (mobCollidesAt(pos.x, pos.y, pos.z)) {
        pos.y -= dy;
        hitY = true;
        break;
      }
    }

    if (hitY) {
      mob.onGround = mob.velocity.y <= 0;
      mob.velocity.y = 0;
    } else {
      mob.onGround = false;
    }

    const lateralStepSpan = underPressure
      ? (highPrecision ? 0.72 : 0.98)
      : (highPrecision ? 0.52 : 0.72);
    const maxLateralSubSteps = underPressure
      ? (highPrecision ? 2 : 1)
      : (highPrecision ? 4 : 3);

    function tryStepUp(targetX, targetZ, inLiquid) {
      if (!(mob.onGround || inLiquid)) return false;
      for (const stepH of stepHeights) {
        const steppedY = pos.y + stepH;
        if (mobCollidesAt(targetX, steppedY, targetZ)) continue;
        pos.y = steppedY;
        return true;
      }
      return false;
    }

    if (Math.abs(dx) > 0.0001) {
      const xSubSteps = Math.max(1, Math.min(maxLateralSubSteps, Math.ceil(Math.abs(dx) / lateralStepSpan)));
      const stepDist = dx / xSubSteps;
      for (let i = 0; i < xSubSteps; i++) {
        const fromX = pos.x;
        const targetX = fromX + stepDist;
        pos.x = targetX;
        if (!mobCollidesAt(pos.x, pos.y, pos.z)) continue;
        pos.x = fromX;
        if (!tryStepUp(targetX, pos.z, isMobInLiquid(mob))) {
          break;
        }
      }
    }

    if (Math.abs(dz) > 0.0001) {
      const zSubSteps = Math.max(1, Math.min(maxLateralSubSteps, Math.ceil(Math.abs(dz) / lateralStepSpan)));
      const stepDist = dz / zSubSteps;
      for (let i = 0; i < zSubSteps; i++) {
        const fromZ = pos.z;
        const targetZ = fromZ + stepDist;
        pos.z = targetZ;
        if (!mobCollidesAt(pos.x, pos.y, pos.z)) continue;
        pos.z = fromZ;
        if (!tryStepUp(pos.x, targetZ, isMobInLiquid(mob))) {
          break;
        }
      }
    }

    // Failsafe: if a lag spike caused a bad overlap, snap back and attempt rescue.
    if (mobCollidesAt(pos.x, pos.y, pos.z)) {
      pos.set(startX, startY, startZ);
      if (mobCollidesAt(pos.x, pos.y, pos.z)) {
        tryResolveMobInsideTerrain(mob, avoidWater);
      }
      mob.velocity.y = Math.min(0, mob.velocity.y);
      mob.onGround = true;
    }

    return dx !== 0 || dz !== 0;
  }

  function hasLineOfSight(fromPos, toPos) {
    losDir.copy(toPos).sub(fromPos);
    const distance = losDir.length();
    if (distance <= 0.0001) return true;

    losDir.multiplyScalar(1 / distance);
    const steps = Math.max(2, Math.ceil(distance / LOS_STEP));
    for (let i = 1; i < steps; i++) {
      losProbe.copy(fromPos).addScaledVector(losDir, (distance * i) / steps);
      if (solidForCollisionAt(worldToBlock(losProbe.x), worldToBlock(losProbe.y), worldToBlock(losProbe.z))) {
        return false;
      }
    }

    return true;
  }

  function getHostileLineOfSightToPlayer(mob, fromPos, toPos, flatDist, delta = 0.016) {
    if (!mob || !fromPos || !toPos) return true;

    const safeDelta = Math.max(0, Number(delta) || 0);
    let cadence = flatDist <= MOB_AI_NEAR_DIST
      ? 0.045
      : flatDist <= MOB_AI_MID_DIST
        ? 0.09
        : 0.16;
    if (runtimePerformanceTier <= 0) cadence *= 1.4;
    else if (runtimePerformanceTier === 1) cadence *= 1.12;
    if (safeDelta > 0.03) cadence *= 1.25;
    const moveEps = flatDist <= MOB_AI_NEAR_DIST ? 0.24 : 0.36;
    const moveEpsSq = moveEps * moveEps;

    mob.losCacheTimer = Math.max(0, Number(mob.losCacheTimer || 0) - safeDelta);
    let shouldRefresh = mob.losCacheTimer <= 0 || typeof mob.losCachedValue !== 'boolean';

    if (!shouldRefresh) {
      const prevMobX = Number(mob.losMobX);
      const prevMobY = Number(mob.losMobY);
      const prevMobZ = Number(mob.losMobZ);
      if (Number.isFinite(prevMobX) && Number.isFinite(prevMobY) && Number.isFinite(prevMobZ)) {
        const mdx = fromPos.x - prevMobX;
        const mdy = fromPos.y - prevMobY;
        const mdz = fromPos.z - prevMobZ;
        if ((mdx * mdx) + (mdy * mdy) + (mdz * mdz) >= moveEpsSq) shouldRefresh = true;
      } else {
        shouldRefresh = true;
      }
    }

    if (!shouldRefresh) {
      const prevPlayerX = Number(mob.losPlayerX);
      const prevPlayerY = Number(mob.losPlayerY);
      const prevPlayerZ = Number(mob.losPlayerZ);
      if (Number.isFinite(prevPlayerX) && Number.isFinite(prevPlayerY) && Number.isFinite(prevPlayerZ)) {
        const pdx = toPos.x - prevPlayerX;
        const pdy = toPos.y - prevPlayerY;
        const pdz = toPos.z - prevPlayerZ;
        if ((pdx * pdx) + (pdy * pdy) + (pdz * pdz) >= moveEpsSq) shouldRefresh = true;
      } else {
        shouldRefresh = true;
      }
    }

    if (mob.type === 'creeper' && mob.fuse > 0.02) shouldRefresh = true;

    if (shouldRefresh) {
      const criticalLosRefresh = mob.cfg?.behavior === 'boss'
        || flatDist <= 1.8
        || (mob.type === 'creeper' && mob.fuse > 0.08);
      if (!criticalLosRefresh && runtimePerformanceTier < 2 && runtimeLosRefreshBudget <= 0) {
        runtimeLosRefreshSkipped += 1;
        mob.losCacheTimer = Math.max(Number(mob.losCacheTimer) || 0, cadence * (runtimePerformanceTier <= 0 ? 1.35 : 1.15));
        return mob.losCachedValue !== false;
      }

      if (!criticalLosRefresh && runtimePerformanceTier < 2) {
        runtimeLosRefreshBudget = Math.max(0, runtimeLosRefreshBudget - 1);
      }

      runtimeLosRefreshUsed += 1;
      mob.losCachedValue = hasLineOfSight(fromPos, toPos);
      mob.losCacheTimer = cadence;
      mob.losMobX = fromPos.x;
      mob.losMobY = fromPos.y;
      mob.losMobZ = fromPos.z;
      mob.losPlayerX = toPos.x;
      mob.losPlayerY = toPos.y;
      mob.losPlayerZ = toPos.z;
    }

    return mob.losCachedValue !== false;
  }

  function getPlayerBlastStats(blastPos) {
    const playerPos = player.getPosition();
    const height = Math.max(1.2, Math.min(2.2, player.getHeight ? player.getHeight() : 1.8));
    const feetY = player.getFeetY ? player.getFeetY() : playerPos.y - height;
    const torsoY = feetY + height * 0.46;

    let visible = 0;
    let total = 0;
    const verticalRatios = [0.12, 0.46, 0.82];

    for (let i = 0; i < verticalRatios.length; i++) {
      const y = feetY + height * verticalRatios[i];
      playerBlastSample.set(playerPos.x, y, playerPos.z);
      total += 1;
      if (hasLineOfSight(blastPos, playerBlastSample)) visible += 1;
    }

    const side = 0.21;
    const sideSamples = [
      [side, 0],
      [-side, 0],
      [0, side],
      [0, -side]
    ];
    for (const [ox, oz] of sideSamples) {
      playerBlastSample.set(playerPos.x + ox, torsoY, playerPos.z + oz);
      total += 1;
      if (hasLineOfSight(blastPos, playerBlastSample)) visible += 1;
    }

    playerBlastCenter.set(playerPos.x, feetY + height * 0.52, playerPos.z);
    return {
      distance: blastPos.distanceTo(playerBlastCenter),
      exposure: total > 0 ? visible / total : 0
    };
  }

  function refreshMobHpBar(mob) {
    const ratio = THREE.MathUtils.clamp(mob.hp / mob.maxHp, 0, 1);
    mob.hpFill.scale.x = Math.max(0.0001, ratio);
    mob.hpFill.position.x = -(0.88 * (1 - ratio)) * 0.5;
    mob.hpFill.material.color.setHex(ratio > 0.5 ? 0x66ff66 : ratio > 0.25 ? 0xffc44d : 0xff4b4b);
    mob.hpRoot.visible = true;
  }

  function applyMobEmissive(mob, isBurning) {
    const hurtPulse = mob.hurtFlash > 0;
    let color = 0x000000;
    let intensity = 0;

    if (mob?.isElite) {
      color = 0x5a3f18;
      intensity = 0.07 + Math.sin(performance.now() * 0.01 + mob.id) * 0.02;
    }

    if (isBurning) {
      color = 0xff6b19;
      intensity = 0.22 + Math.sin(performance.now() * 0.025) * 0.06;
    }

    if (hurtPulse) {
      color = 0xff2e2e;
      intensity = Math.max(intensity, 0.45);
    }

    for (const mat of mob.mats) {
      mat.emissive.setHex(color);
      mat.emissiveIntensity = intensity;
    }
  }

  function rollDropCount(dropDef) {
    if (!dropDef) return 0;
    if (typeof dropDef.chance === 'number' && Math.random() > Math.max(0, Math.min(1, dropDef.chance))) return 0;
    const min = Math.max(0, Math.floor(dropDef.min || 1));
    const max = Math.max(min, Math.floor(dropDef.max || min));
    return min + Math.floor(Math.random() * (max - min + 1));
  }

  function maybePromoteElite(mob) {
    if (!mob || mob.cfg?.behavior !== 'hostile') return;
    if (mob.type === 'ender_dragon') return;

    const ecosystem = getEcosystemAt(mob.mesh.position.x, mob.mesh.position.z);
    const threat = Math.max(0, Number(ecosystem?.threat || 0));
    const eliteChance = 0.025 + threat * 0.025 + (ecosystem?.shipwreck ? 0.015 : 0);
    if (Math.random() > eliteChance) return;

    mob.isElite = true;
    mob.attackScale = 1.2 + threat * 0.08;
    const hpScale = 1.4 + threat * 0.12;
    mob.maxHp = Math.max(mob.maxHp + 4, Math.round(mob.maxHp * hpScale));
    mob.hp = mob.maxHp;
    mob.eliteDrop = threat >= 2 ? 'emerald' : 'iron_ingot';
    refreshMobHpBar(mob);
  }

  function startMobDeath(mob, cause = 'killed') {
    const idx = mobs.indexOf(mob);
    if (idx < 0) return;

    mobs.splice(idx, 1);
    hitboxToMob.delete(mob.hitbox.uuid);
    if (mob.type === 'ender_dragon') endDragonDefeated = true;

    const drops = Array.isArray(mob.cfg.drop)
      ? mob.cfg.drop
      : (mob.cfg.drop ? [mob.cfg.drop] : []);
    if (drops.length > 0 && onMobDrop) {
      for (const drop of drops) {
        if (!drop?.itemId) continue;
        const count = rollDropCount(drop);
        if (count <= 0) continue;
        onMobDrop({
          type: mob.type,
          cause,
          itemId: drop.itemId,
          count,
          position: mob.mesh.position.clone()
        });
      }
    }
    if (mob.isElite && onMobDrop) {
      const bonusItem = mob.eliteDrop || 'iron_ingot';
      const bonusCount = 1 + (Math.random() < 0.45 ? 1 : 0);
      onMobDrop({
        type: mob.type,
        cause: 'elite_bonus',
        itemId: bonusItem,
        count: bonusCount,
        position: mob.mesh.position.clone()
      });
    }

    mob.hitbox.visible = false;
    mob.hpRoot.visible = false;
    if (mob.nameTag) mob.nameTag.visible = false;

    deadMobs.push({
      mesh: mob.mesh,
      mats: mob.mats,
      type: mob.type,
      ttl: 0.48,
      duration: 0.48,
      baseY: mob.mesh.position.y,
      spin: (Math.random() * 2 - 1) * 1.6,
      cause
    });

    if (onMobDeath) {
      onMobDeath({
        type: mob.type,
        cause,
        position: mob.mesh.position.clone()
      });
    }
  }

  function damageMob(mob, amount, cause = 'killed') {
    if (!mob || amount <= 0 || mob.hp <= 0) return { hit: false, killed: false };

    const prevHp = mob.hp;
    mob.hp = Math.max(0, mob.hp - amount);
    const dealt = Math.max(0, prevHp - mob.hp);
    if (dealt <= 0) return { hit: false, killed: false };
    mob.hurtFlash = 0.28;
    if (cause === 'player' && mob.type === 'spider') {
      mob.aggroTime = Math.max(mob.aggroTime || 0, 14.0);
    }
    if (cause === 'player' && mob.type === 'enderman') {
      mob.aggroTime = Math.max(mob.aggroTime || 0, 22.0);
    }
    refreshMobHpBar(mob);

    if (mob.hp <= 0) {
      if (onMobDamaged && cause === 'player') {
        onMobDamaged({
          type: mob.type,
          cause,
          damage: dealt,
          hp: 0,
          maxHp: mob.maxHp,
          killed: true,
          position: mob.mesh.position.clone()
        });
      }
      startMobDeath(mob, cause);
      return { hit: true, killed: true, damage: dealt };
    }

    if (onMobDamaged && cause === 'player') {
      onMobDamaged({
        type: mob.type,
        cause,
        damage: dealt,
        hp: mob.hp,
        maxHp: mob.maxHp,
        killed: false,
        position: mob.mesh.position.clone()
      });
    }

    if (mob.type === 'enderman' && cause === 'player') {
      const shouldBlink = Math.random() < 0.46;
      if (shouldBlink) {
        const playerPos = player.getPosition();
        if (tryTeleportMobNear(mob, playerPos, 6, 14, true, 16)) {
          mob.attackCooldown = Math.max(mob.attackCooldown, 0.2);
          mob.blinkCooldown = 1.8 + Math.random() * 2.4;
        }
      }
    }

    return { hit: true, killed: false, damage: dealt };
  }

  function spawnMob(type, x, y, z, opts = {}) {
    const cfg = MOB_CONFIG[type];
    if (!cfg) return null;

    const built = createMobMesh(type);
    if (!built) return null;

    built.group.position.set(x, y, z);
    scene.add(built.group);

    const mob = {
      id: mobSeq++,
      type,
      cfg,
      dimension: opts.dimension || world.getDimensionAt(x, z),
      mesh: built.group,
      hitbox: built.hitbox,
      hpRoot: built.hpRoot,
      hpFill: built.hpFill,
      mats: built.mats,
      parts: built.parts || null,
      hp: cfg.hp,
      maxHp: cfg.hp,
      name: null,
      nameTag: null,
      attackScale: 1,
      attackCooldown: Math.random() * 0.6,
      shootCooldown: Math.random() * 1.2,
      fuse: 0,
      burnTime: 0,
      lavaTick: 0,
      hurtFlash: 0,
      wanderDir: new THREE.Vector3(0, 0, 1),
      wanderTime: 0,
      fleeTime: 0,
      blinkCooldown: 1.8 + Math.random() * 2.2,
      aggroTime: 0,
      aiAccum: Math.random() * 0.01,
      voiceCooldown: getAmbientCooldown(type, cfg.behavior),
      spawnGrace: MOB_SPAWN_GRACE_SECONDS,
      visualRetainUntil: 0,
      lastFuseAudioStep: -1,
      bossTime: 0,
      orbitAngle: Math.random() * Math.PI * 2,
      orbitRadius: 20 + Math.random() * 8,
      swoopTimer: 0,
      swoopCooldown: 3.4 + Math.random() * 2.2,
      isBaby: opts.isBaby === true,
      babyGrowTime: opts.isBaby === true ? PASSIVE_BABY_GROW_DURATION : 0,
      loveTime: 0,
      breedCooldown: 0,
      stuckAccum: 0,
      prevPosX: x,
      prevPosZ: z
    };

    if (type === 'villager') {
      const idx = Math.floor(Math.random() * VILLAGER_PROFESSIONS.length);
      mob.profession = VILLAGER_PROFESSIONS[Math.max(0, Math.min(VILLAGER_PROFESSIONS.length - 1, idx))];
      const home = resolveVillagerHomeAt(x, z, mob.dimension);
      if (home) {
        mob.homeX = home.centerX;
        mob.homeZ = home.centerZ;
        mob.homeRadius = home.radius;
      } else {
        mob.homeX = x;
        mob.homeZ = z;
        mob.homeRadius = 12;
      }
    }

    if (type === 'ender_dragon') {
      mob.attackCooldown = 0.7;
      mob.shootCooldown = 0;
      mob.swoopTimer = 0;
      mob.swoopCooldown = 2.6;
    } else if (type === 'enderman') {
      mob.attackCooldown = 0.55 + Math.random() * 0.35;
      mob.shootCooldown = 0;
      mob.blinkCooldown = 1.4 + Math.random() * 1.8;
    }

    if (mob.isBaby && cfg.behavior === 'passive') {
      mob.mesh.scale.setScalar(BABY_MOB_SCALE);
      mob.maxHp = Math.max(4, Math.round(cfg.hp * 0.55));
      mob.hp = mob.maxHp;
    }

    refreshMobDisplayName(mob);
    mob.hitbox.layers.set(1);

    maybePromoteElite(mob);
    refreshMobHpBar(mob);
    hitboxToMob.set(mob.hitbox.uuid, mob);
    mobs.push(mob);
    return mob;
  }

  function findClosestBreedPartner(mob, maxDistance = PASSIVE_BREED_FOLLOW_RANGE) {
    if (!mob || !isBreedablePassiveType(mob.type)) return null;

    const maxDistSq = Math.max(0.1, maxDistance) * Math.max(0.1, maxDistance);
    let closest = null;
    let closestDistSq = maxDistSq;

    for (const other of mobs) {
      if (other === mob) continue;
      if (other.type !== mob.type) continue;
      if (other.dimension !== mob.dimension) continue;
      if (other.isBaby || other.babyGrowTime > 0) continue;
      if (other.loveTime <= 0 || other.breedCooldown > 0) continue;

      breedSearchVec.copy(other.mesh.position).sub(mob.mesh.position);
      breedSearchVec.y = 0;
      const distSq = breedSearchVec.lengthSq();
      if (distSq >= closestDistSq) continue;

      closestDistSq = distSq;
      closest = other;
    }

    return closest;
  }

  function trySpawnBabyFromParents(first, second) {
    if (!first || !second || first === second) return null;
    if (!isBreedablePassiveType(first.type) || first.type !== second.type) return null;
    if (first.dimension !== second.dimension) return null;
    if (first.isBaby || second.isBaby) return null;
    if (first.babyGrowTime > 0 || second.babyGrowTime > 0) return null;
    if (first.loveTime <= 0 || second.loveTime <= 0) return null;
    if (first.breedCooldown > 0 || second.breedCooldown > 0) return null;
    if (countBehavior('passive', first.dimension) >= 36) return null;

    feedPos.copy(first.mesh.position).add(second.mesh.position).multiplyScalar(0.5);
    const spawnX = feedPos.x + (Math.random() * 0.8 - 0.4);
    const spawnZ = feedPos.z + (Math.random() * 0.8 - 0.4);
    const spawnY = findWalkableMobY(
      spawnX,
      spawnZ,
      Math.max(first.mesh.position.y, second.mesh.position.y),
      true
    );
    if (spawnY === null) return null;

    const baby = spawnMob(first.type, spawnX, spawnY, spawnZ, {
      dimension: first.dimension,
      isBaby: true
    });
    if (!baby) return null;

    first.loveTime = 0;
    second.loveTime = 0;
    first.breedCooldown = PASSIVE_BREED_COOLDOWN;
    second.breedCooldown = PASSIVE_BREED_COOLDOWN;
    first.fleeTime = 0;
    second.fleeTime = 0;
    first.wanderTime = 0.4;
    second.wanderTime = 0.4;
    first.wanderDir.set(Math.random() * 2 - 1, 0, Math.random() * 2 - 1).normalize();
    second.wanderDir.set(Math.random() * 2 - 1, 0, Math.random() * 2 - 1).normalize();

    return baby;
  }

  function tryFeedPassiveMob(origin, direction, itemId, {
    maxDistance = 4.5,
    consumeItem = null
  } = {}) {
    const itemKey = String(itemId || '');
    if (!itemKey || !origin || !direction) return { fed: false };
    if (mobs.length <= 0) return { fed: false };

    const playerDimension = world.getDimensionAt(origin.x, origin.z);
    const hitboxes = [];
    for (const mob of mobs) {
      if (mob.dimension !== playerDimension) continue;
      if (mob.cfg.behavior !== 'passive') continue;
      if (!isBreedablePassiveType(mob.type)) continue;
      hitboxes.push(mob.hitbox);
    }
    if (hitboxes.length <= 0) return { fed: false };

    raycaster.set(origin, direction);
    raycaster.far = Math.max(1.2, maxDistance);
    const hits = raycaster.intersectObjects(hitboxes, false);
    if (hits.length <= 0) return { fed: false };

    for (const hit of hits) {
      const mob = hitboxToMob.get(hit.object.uuid);
      if (!mob || mob.dimension !== playerDimension) continue;
      if (mob.cfg.behavior !== 'passive' || !isBreedablePassiveType(mob.type)) continue;
      if (!canFeedMobType(mob.type, itemKey)) continue;

      if (mob.isBaby || mob.babyGrowTime > 0) {
        return {
          fed: false,
          reason: 'baby',
          mobType: mob.type
        };
      }
      if (mob.breedCooldown > 0) {
        return {
          fed: false,
          reason: 'cooldown',
          mobType: mob.type
        };
      }
      if (mob.loveTime > 0) {
        return {
          fed: false,
          reason: 'already_love',
          mobType: mob.type
        };
      }

      if (typeof consumeItem === 'function') {
        const consumed = consumeItem();
        if (!consumed) {
          return {
            fed: false,
            reason: 'consume_failed',
            mobType: mob.type
          };
        }
      }

      mob.hp = Math.min(mob.maxHp, mob.hp + PASSIVE_FEED_HEAL);
      refreshMobHpBar(mob);
      mob.loveTime = PASSIVE_LOVE_DURATION;
      mob.fleeTime = 0;
      mob.wanderTime = 0;

      const partner = findClosestBreedPartner(mob, PASSIVE_BREED_FOLLOW_RANGE);
      const babyBorn = !!(partner && trySpawnBabyFromParents(mob, partner));
      emitMobSound(mob, 'ambient', { distance: 0 });

      return {
        fed: true,
        babyBorn,
        mobType: mob.type
      };
    }

    return { fed: false };
  }

  function spawnArrowProjectile(fromPos, velocity, owner = 'mob', damage = 6, sourceType = 'skeleton_archer') {
    const mesh = new THREE.Mesh(
      arrowGeo,
      new THREE.MeshStandardMaterial({ color: 0x5f5f5f, roughness: 0.8, metalness: 0.12 })
    );
    mesh.position.copy(fromPos);
    scene.add(mesh);

    arrows.push({
      mesh,
      vel: velocity.clone(),
      dimension: getDimensionAtPos(fromPos),
      owner,
      sourceType,
      damage,
      ttl: owner === 'player' ? 5.2 : 4.2
    });
  }

  function spawnMobArrow(fromPos, toPos, damage = 6, sourceType = 'skeleton_archer') {
    const dir = toPos.clone().sub(fromPos).normalize();
    spawnArrowProjectile(fromPos, dir.multiplyScalar(8.5), 'mob', damage, sourceType);
  }

  function spawnPlayerArrow(fromPos, direction, speed = 18, damage = 10) {
    if (!fromPos || !direction) return false;
    const dir = direction.clone().normalize();
    if (dir.lengthSq() < 0.0001) return false;
    spawnArrowProjectile(fromPos, dir.multiplyScalar(Math.max(6, speed)), 'player', damage);
    return true;
  }

  function spawnExplosionFx(pos) {
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.4, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xffcc55, transparent: true, opacity: 0.75 })
    );
    mesh.position.copy(pos);
    scene.add(mesh);
    explosionFx.push({ mesh, ttl: 0.35 });
  }

  function countBehavior(behavior, dimension = null) {
    let total = 0;
    for (const mob of mobs) {
      if (mob.cfg.behavior !== behavior) continue;
      if (dimension && mob.dimension !== dimension) continue;
      total += 1;
    }
    return total;
  }

  function normalizeSpawnWeights(entries) {
    const list = Array.isArray(entries) ? entries : [];
    if (list.length <= 0) return [];

    let total = 0;
    for (const entry of list) {
      total += Math.max(0, Number(entry?.w) || 0);
    }

    if (total <= 0) {
      const fallback = 1 / list.length;
      return list.map((entry) => ({
        type: entry?.type || 'zombie',
        w: fallback
      }));
    }

    return list.map((entry) => ({
      type: entry?.type || 'zombie',
      w: Math.max(0, Number(entry?.w) || 0) / total
    }));
  }

  function getZombieSpawnMultiplierByDay(dayCount = 1) {
    const day = Math.max(1, Math.floor(dayCount || 1));
    if (day <= 3) return 0.35 + (day - 1) * 0.07;
    if (day <= 7) return 0.49 + (day - 3) * 0.08;
    if (day <= 14) return 0.81 + (day - 7) * 0.03;
    if (day <= 28) return 1.02 + (day - 14) * 0.012;
    return 1.2;
  }

  function getEndermanSpawnMultiplierByDay(dayCount = 1, dimension = 'overworld') {
    if (dimension === 'end') return 1;
    const day = Math.max(1, Math.floor(dayCount || 1));
    if (day < ENDERMAN_UNLOCK_DAY) return 0;
    if (day >= ENDERMAN_FULL_WEIGHT_DAY) return 1;
    return (day - ENDERMAN_UNLOCK_DAY + 1) / (ENDERMAN_FULL_WEIGHT_DAY - ENDERMAN_UNLOCK_DAY + 1);
  }

  function applyProgressionSpawnGates(entries, dayCount = 1, dimension = 'overworld') {
    const endermanMultiplier = getEndermanSpawnMultiplierByDay(dayCount, dimension);
    const scaled = [];

    for (const entry of entries || []) {
      if (!entry?.type) continue;
      const baseWeight = Math.max(0, Number(entry.w) || 0);
      const multiplier = entry.type === 'enderman' ? endermanMultiplier : 1;
      const weight = baseWeight * multiplier;
      if (weight <= 0) continue;
      scaled.push({
        type: entry.type,
        w: weight
      });
    }

    return normalizeSpawnWeights(scaled);
  }

  function applyHostileDayScaling(entries, dayCount = 1, dimension = 'overworld') {
    const zombieMultiplier = getZombieSpawnMultiplierByDay(dayCount);
    const scaled = [];
    for (const entry of entries || []) {
      if (!entry?.type) continue;
      const baseWeight = Math.max(0, Number(entry.w) || 0);
      scaled.push({
        type: entry.type,
        w: entry.type === 'zombie' ? baseWeight * zombieMultiplier : baseWeight
      });
    }
    return applyProgressionSpawnGates(scaled, dayCount, dimension);
  }

  function getOverworldHostileDayTuning(dayCount = 1) {
    const day = Math.max(1, Math.floor(dayCount || 1));

    let earlyIntervalOffset = 0;
    let earlyCapOffset = 0;
    if (day <= 1) {
      earlyIntervalOffset = 2.1;
      earlyCapOffset = -3;
    } else if (day <= 2) {
      earlyIntervalOffset = 1.6;
      earlyCapOffset = -2;
    } else if (day <= 4) {
      earlyIntervalOffset = 1.0;
      earlyCapOffset = -2;
    } else if (day <= 7) {
      earlyIntervalOffset = 0.5;
      earlyCapOffset = -1;
    } else if (day <= 10) {
      earlyIntervalOffset = 0.18;
    }

    const progression = THREE.MathUtils.clamp((day - 9) / 21, 0, 1);
    const lateIntervalOffset = -progression * 0.55;
    const lateCapOffset = Math.min(4, Math.max(0, Math.floor((day - 10) / 5)));

    return {
      intervalOffset: earlyIntervalOffset + lateIntervalOffset,
      capOffset: earlyCapOffset + lateCapOffset
    };
  }

  function isWithinWorldSpawnSafeZone(x, z, radiusSq = NEWBIE_WORLD_SPAWN_SAFE_RADIUS_SQ) {
    const dx = Number(x) || 0;
    const dz = Number(z) || 0;
    return ((dx * dx) + (dz * dz)) <= radiusSq;
  }

  function trySpawnNearPlayer(playerPos, entries, minDist, maxDist, spawnTypeFilter = null, dimension = null, spawnRule = null, attempts = 6) {
    const targetDimension = dimension || getDimensionAtPos(playerPos);
    const maxAttempts = Math.max(1, Math.floor(attempts || 6));
    for (let i = 0; i < maxAttempts; i++) {
      const type = spawnTypeFilter || pickWeighted(entries);
      const cfg = MOB_CONFIG[type];
      if (!cfg) continue;

      const angle = Math.random() * Math.PI * 2;
      const dist = minDist + Math.random() * (maxDist - minDist);
      const x = playerPos.x + Math.cos(angle) * dist;
      const z = playerPos.z + Math.sin(angle) * dist;
      if (world.getDimensionAt(x, z) !== targetDimension) continue;
      const surfaceY = world.getSurfaceHeight(x, z);
      const spawnY = surfaceY + 0.5;
      const bx = worldToBlock(x);
      const by = worldToBlock(spawnY);
      const bz = worldToBlock(z);

      spawnPos.set(x, spawnY, z);

      const inWater = world.isWaterAt(bx, by, bz);
      const spawnInWater = cfg.spawnInWater === true;
      const preferWater = spawnInWater && cfg.preferWater !== false;
      if (spawnInWater) {
        if (preferWater && !inWater) continue;
        if (!inWater && !solidForCollisionAt(bx, by - 1, bz)) continue;
      } else {
        if (inWater) continue;
        if (!solidForCollisionAt(bx, by - 1, bz)) continue;
      }
      if (mobCollidesAt(x, spawnY, z)) continue;
      if (playerPos.distanceToSquared(spawnPos) < minDist * minDist) continue;
      if (typeof spawnRule === 'function' && !spawnRule({ x, y: spawnY, z, dimension: targetDimension })) continue;

      let blocked = false;
      for (const other of mobs) {
        if (other.dimension !== targetDimension) continue;
        if (other.mesh.position.distanceToSquared(spawnPos) < 4.4) {
          blocked = true;
          break;
        }
      }
      if (blocked) continue;

      if (type === 'villager' && !isVillageNearby(x, z, targetDimension)) continue;
      spawnMob(type, x, spawnY, z, { dimension: targetDimension });
      return true;
    }

    return false;
  }

  function hurtMobsInRadius(center, radius, damage) {
    for (let i = mobs.length - 1; i >= 0; i--) {
      const mob = mobs[i];
      const d = center.distanceTo(mob.mesh.position);
      if (d > radius) continue;
      const dealt = damage * (1 - d / radius);
      damageMob(mob, dealt, 'explosion');
    }
  }

  function isSunlit(pos) {
    const bx = Math.round(pos.x);
    const bz = Math.round(pos.z);
    const topY = Math.round(pos.y + 2.0);

    if (world.isWaterAt(bx, topY, bz)) return false;

    for (let y = topY; y <= topY + 18; y++) {
      const id = world.getBlockIdAt(bx, y, bz);
      if (id && id !== 7 && id !== 12) return false;
    }
    return true;
  }

  function hasNearbyLightSource(x, y, z, radius = 5) {
    const cx = worldToBlock(x);
    const cy = worldToBlock(y);
    const cz = worldToBlock(z);
    const r = Math.max(1, Math.floor(radius));
    const cacheKey = `${cx},${cy},${cz},${r}`;
    if (lightQueryCache.has(cacheKey)) return lightQueryCache.get(cacheKey);
    const rSq = r * r;

    for (let bz = cz - r; bz <= cz + r; bz++) {
      for (let by = cy - r; by <= cy + r; by++) {
        for (let bx = cx - r; bx <= cx + r; bx++) {
          const dx = bx - cx;
          const dy = by - cy;
          const dz = bz - cz;
          if ((dx * dx) + (dy * dy) + (dz * dz) > rSq) continue;
          const id = world.getBlockIdAt(bx, by, bz);
          if (HOSTILE_LIGHT_BLOCK_IDS.has(id)) {
            lightQueryCache.set(cacheKey, true);
            return true;
          }
        }
      }
    }

    lightQueryCache.set(cacheKey, false);
    return false;
  }

  function getPoiAt(x, z) {
    if (typeof world.getPointOfInterestAt !== 'function') return null;
    return world.getPointOfInterestAt(x, z);
  }

  function getEcosystemAt(x, z) {
    const bx = worldToBlock(x);
    const bz = worldToBlock(z);
    const cacheKey = `${bx},${bz}`;
    if (ecosystemQueryCache.has(cacheKey)) return ecosystemQueryCache.get(cacheKey);

    let result = null;
    if (typeof world.getEcosystemAt === 'function') {
      result = world.getEcosystemAt(bx, bz);
    } else {
      result = {
        poi: getPoiAt(bx, bz),
        shipwreck: null,
        settlement: null,
        biome: null,
        humidity: 0.5
      };
    }
    ecosystemQueryCache.set(cacheKey, result);
    return result;
  }

  function isThreatPoi(poi) {
    if (!poi) return false;
    return poi.type === 'monster_den' || poi.type === 'raider_outpost';
  }

  function isFriendlyPoi(poi) {
    if (!poi) return false;
    return poi.type === 'hamlet' || poi.type === 'tribe_camp' || poi.type === 'hospital';
  }

  function isVillageNearby(x, z, dimension = 'overworld') {
    if (dimension !== 'overworld') return false;

    const ecosystem = getEcosystemAt(x, z);
    if (ecosystem?.settlement) return true;
    if (ecosystem?.poi && isFriendlyPoi(ecosystem.poi)) return true;

    if (typeof world.getSettlementAt === 'function') {
      const settlement = world.getSettlementAt(x, z);
      if (settlement) return true;
    }
    const poi = getPoiAt(x, z);
    if (poi && isFriendlyPoi(poi)) return true;

    // Modern world API path already provides deterministic village/POI presence.
    // Only fall back to local block scanning when those APIs are unavailable.
    if (
      typeof world.getEcosystemAt === 'function' ||
      typeof world.getSettlementAt === 'function' ||
      typeof world.getPointOfInterestAt === 'function'
    ) {
      return false;
    }

    const cx = worldToBlock(x);
    const cz = worldToBlock(z);
    let score = 0;
    const radius = 10;

    for (let dz = -radius; dz <= radius; dz += 2) {
      for (let dx = -radius; dx <= radius; dx += 2) {
        const sx = cx + dx;
        const sz = cz + dz;
        if (world.getDimensionAt(sx, sz) !== 'overworld') continue;

        const surfaceY = Math.round(world.getSurfaceHeight(sx, sz));
        const groundId = world.getBlockIdAt(sx, surfaceY, sz);
        const aboveId = world.getBlockIdAt(sx, surfaceY + 1, sz);
        const above2Id = world.getBlockIdAt(sx, surfaceY + 2, sz);

        if (VILLAGER_NEARBY_BLOCK_IDS.has(groundId)) {
          score += (groundId === 25 || groundId === 24 || groundId === 21 || groundId === 22) ? 1.2 : 0.55;
        }
        if (VILLAGER_NEARBY_BLOCK_IDS.has(aboveId)) {
          score += (aboveId === 16 || aboveId === 17 || aboveId === 20 || aboveId === 12) ? 1.9 : 0.95;
        }
        if (VILLAGER_NEARBY_BLOCK_IDS.has(above2Id)) {
          score += (above2Id === 12 || above2Id === 20) ? 1.3 : 0.6;
        }

        if (score >= 7.2) return true;
      }
    }

    return false;
  }

  function resolveVillagerHomeAt(x, z, dimension = 'overworld') {
    if (dimension !== 'overworld') return null;

    const ecosystem = getEcosystemAt(x, z);
    const settlement = ecosystem?.settlement || (typeof world.getSettlementAt === 'function' ? world.getSettlementAt(x, z) : null);
    if (settlement && Number.isFinite(settlement.centerX) && Number.isFinite(settlement.centerZ)) {
      return {
        centerX: settlement.centerX,
        centerZ: settlement.centerZ,
        radius: Math.max(10, Number(settlement.radius) || 16)
      };
    }

    const poi = ecosystem?.poi || getPoiAt(x, z);
    if (poi && isFriendlyPoi(poi) && Number.isFinite(poi.centerX) && Number.isFinite(poi.centerZ)) {
      return {
        centerX: poi.centerX,
        centerZ: poi.centerZ,
        radius: Math.max(8, Number(poi.radius) || 12)
      };
    }

    return null;
  }

  function clearAll() {
    for (const mob of mobs) {
      if (mob.nameTag) disposeMobNameTag(mob.nameTag);
      hitboxToMob.delete(mob.hitbox.uuid);
      scene.remove(mob.mesh);
    }
    for (const dead of deadMobs) scene.remove(dead.mesh);
    for (const arrow of arrows) scene.remove(arrow.mesh);
    for (const fx of explosionFx) scene.remove(fx.mesh);

    mobs.length = 0;
    deadMobs.length = 0;
    arrows.length = 0;
    explosionFx = [];
    endDragonDefeated = false;
    ecosystemQueryCache.clear();
    lightQueryCache.clear();
  }

  function tryPlayerAttack(origin, direction, damage, maxDistance = 3.4) {
    if (mobs.length === 0) return { hit: false };
    const playerDimension = world.getDimensionAt(origin.x, origin.z);
    const hitboxes = [];
    for (const mob of mobs) {
      if (mob.dimension !== playerDimension) continue;
      hitboxes.push(mob.hitbox);
    }
    if (hitboxes.length === 0) return { hit: false };

    raycaster.set(origin, direction);
    raycaster.far = maxDistance;

    const hits = raycaster.intersectObjects(hitboxes, false);
    if (hits.length === 0) return { hit: false };

    for (const hit of hits) {
      const mob = hitboxToMob.get(hit.object.uuid);
      if (!mob) continue;
      if (mob.dimension !== playerDimension) continue;

      const result = damageMob(mob, damage, 'player');
      if (!result.killed) {
        tempVec.copy(mob.mesh.position).sub(origin);
        tempVec.y = 0;
        const len = tempVec.length();
        if (len > 0.0001) {
          tempVec.multiplyScalar(0.5 / len);
          mob.mesh.position.add(tempVec);
          if (!mob.velocity) mob.velocity = new THREE.Vector3(0, 0, 0);
          mob.velocity.y = Math.max(mob.velocity.y, 3.0);
        }

        if (mob.cfg.behavior === 'passive') {
          mob.fleeTime = Math.max(mob.fleeTime, 1.8);
        }
      }

      return {
        hit: true,
        killed: result.killed,
        mobType: mob.type,
        distance: hit.distance,
        hitPoint: hit.point.clone(),
        hp: Math.max(0, mob.hp),
        maxHp: mob.maxHp
      };
    }

    return { hit: false };
  }

  function tryInteractVillager(origin, direction, maxDistance = 4.5) {
    if (mobs.length === 0) return { hit: false };
    const playerDimension = world.getDimensionAt(origin.x, origin.z);
    const hitboxes = [];
    for (const mob of mobs) {
      if (mob.dimension !== playerDimension) continue;
      if (mob.type !== 'villager') continue;
      hitboxes.push(mob.hitbox);
    }
    if (hitboxes.length === 0) return { hit: false };

    raycaster.set(origin, direction);
    raycaster.far = Math.max(1.2, maxDistance);
    const hits = raycaster.intersectObjects(hitboxes, false);
    if (hits.length === 0) return { hit: false };

    for (const hit of hits) {
      const mob = hitboxToMob.get(hit.object.uuid);
      if (!mob || mob.type !== 'villager') continue;
      if (mob.dimension !== playerDimension) continue;
      return {
        hit: true,
        distance: hit.distance,
        villager: {
          id: String(mob.id),
          profession: mob.profession || 'farmer',
          name: mob.name || tr('villager.default_name', {}, 'Villager'),
          position: mob.mesh.position.clone()
        }
      };
    }

    return { hit: false };
  }

  function updatePassiveMob(mob, delta, toPlayer, flatDist) {
    const pos = mob.mesh.position;
    const villagerWithHome = mob.type === 'villager' && Number.isFinite(mob.homeX) && Number.isFinite(mob.homeZ);
    const landMobAvoidsWater = mob.cfg.spawnInWater !== true;
    const inWater = landMobAvoidsWater && isMobInWater(mob);
    let toHomeX = 0;
    let toHomeZ = 0;
    let homeDist = 0;
    if (villagerWithHome) {
      toHomeX = mob.homeX - pos.x;
      toHomeZ = mob.homeZ - pos.z;
      homeDist = Math.hypot(toHomeX, toHomeZ);

      const hardLimit = (mob.homeRadius || 12) + 9;
      if (homeDist > hardLimit) {
        const homeY = findWalkableMobY(mob.homeX, mob.homeZ, pos.y, true);
        if (homeY !== null) {
          pos.set(mob.homeX, homeY, mob.homeZ);
        } else {
          tryTeleportMobNear(
            mob,
            { x: mob.homeX, y: pos.y, z: mob.homeZ },
            0.5,
            Math.max(2.5, Math.min(8, (mob.homeRadius || 12) * 0.55)),
            true,
            20
          );
        }
        mob.fleeTime = Math.min(mob.fleeTime, 0.2);
        toHomeX = mob.homeX - pos.x;
        toHomeZ = mob.homeZ - pos.z;
        homeDist = Math.hypot(toHomeX, toHomeZ);
      }
    }

    const inLove = mob.loveTime > 0 && !mob.isBaby && isBreedablePassiveType(mob.type);

    if (!inLove && flatDist < 6.5) {
      mob.fleeTime = Math.max(mob.fleeTime, 0.45);
    }

    mob.wanderTime -= delta;
    mob.fleeTime = Math.max(0, mob.fleeTime - delta);

    let moveX = 0;
    let moveZ = 0;

    if (mob.fleeTime > 0 && flatDist > 0.001) {
      moveX = -toPlayer.x / flatDist;
      moveZ = -toPlayer.z / flatDist;
    } else if (inLove) {
      const partner = findClosestBreedPartner(mob, PASSIVE_BREED_FOLLOW_RANGE);
      if (partner) {
        breedSearchVec.copy(partner.mesh.position).sub(pos);
        const partnerDist = Math.hypot(breedSearchVec.x, breedSearchVec.z);
        if (partnerDist > PASSIVE_BREED_RANGE) {
          moveX = breedSearchVec.x / Math.max(0.0001, partnerDist);
          moveZ = breedSearchVec.z / Math.max(0.0001, partnerDist);
        }
        if (partnerDist <= PASSIVE_BREED_RANGE + 0.22) {
          trySpawnBabyFromParents(mob, partner);
        }
      } else {
        if (mob.wanderTime <= 0 || mob.wanderDir.lengthSq() < 0.01) {
          const angle = Math.random() * Math.PI * 2;
          mob.wanderDir.set(Math.cos(angle), 0, Math.sin(angle));
          mob.wanderTime = 0.8 + Math.random() * 1.6;
        }
        moveX = mob.wanderDir.x;
        moveZ = mob.wanderDir.z;
      }
    } else {
      if (mob.wanderTime <= 0 || mob.wanderDir.lengthSq() < 0.01) {
        const angle = Math.random() * Math.PI * 2;
        mob.wanderDir.set(Math.cos(angle), 0, Math.sin(angle));
        mob.wanderTime = 1.6 + Math.random() * 2.4;
      }
      moveX = mob.wanderDir.x;
      moveZ = mob.wanderDir.z;
    }

    if (villagerWithHome && homeDist > 0.001) {
      const softLimit = (mob.homeRadius || 12) + 1.5;
      if (homeDist > softLimit) {
        const homeNX = toHomeX / homeDist;
        const homeNZ = toHomeZ / homeDist;
        const pull = THREE.MathUtils.clamp((homeDist - softLimit) / Math.max(2, mob.homeRadius || 12), 0.24, 0.95);
        moveX = THREE.MathUtils.lerp(moveX, homeNX, pull);
        moveZ = THREE.MathUtils.lerp(moveZ, homeNZ, pull);
        const n = Math.hypot(moveX, moveZ) || 1;
        moveX /= n;
        moveZ /= n;
        if (homeDist > softLimit + 3.0) {
          mob.fleeTime = Math.min(mob.fleeTime, 0.28);
        }
      }
    }

    let escapingWater = false;
    if (inWater) {
      mob.waterEscapeRepathCd = Math.max(0, Number(mob.waterEscapeRepathCd || 0) - delta);
      const shouldRepath = mob.waterEscapeRepathCd <= 0
        || !Number.isFinite(mob.waterEscapeDirX)
        || !Number.isFinite(mob.waterEscapeDirZ);
      if (shouldRepath) {
        if (findLandEscapeDirectionFromWater(mob, waterEscapeDir)) {
          mob.waterEscapeDirX = waterEscapeDir.x;
          mob.waterEscapeDirZ = waterEscapeDir.y;
        } else {
          mob.waterEscapeDirX = 0;
          mob.waterEscapeDirZ = 0;
        }
        mob.waterEscapeRepathCd = 0.26 + Math.random() * 0.24;
      }

      const ex = Number(mob.waterEscapeDirX || 0);
      const ez = Number(mob.waterEscapeDirZ || 0);
      if (Math.hypot(ex, ez) > 0.0001) {
        moveX = ex;
        moveZ = ez;
        escapingWater = true;
      } else {
        moveX = 0;
        moveZ = 0;
      }
      mob.wanderTime = Math.min(mob.wanderTime, 0.24);
    } else {
      mob.waterEscapeRepathCd = 0;
      mob.waterEscapeDirX = 0;
      mob.waterEscapeDirZ = 0;
    }

    const fleeBoost = mob.type === 'villager' ? 0.98 : 1.22;
    const roamSpeed = mob.type === 'villager' ? 0.58 : 0.8;
    const waterEscapeBoost = escapingWater ? 1.28 : 1.0;
    const speed = mob.cfg.speed * (mob.fleeTime > 0 ? fleeBoost : (inLove ? 0.92 : roamSpeed)) * waterEscapeBoost;
    const step = speed * delta;
    const avoidWater = landMobAvoidsWater;
    moveMobWithCollision(mob, moveX * step, moveZ * step, delta, 8.5, avoidWater, flatDist);

    const lookX = escapingWater ? moveX : (mob.fleeTime > 0 ? -moveX : moveX);
    const lookZ = escapingWater ? moveZ : (mob.fleeTime > 0 ? -moveZ : moveZ);
    if (Math.hypot(lookX, lookZ) > 0.0001) {
      mob.mesh.rotation.y = Math.atan2(lookX, lookZ);
    }
  }

  function updateHostileMob(
    mob,
    delta,
    isNight,
    toPlayer,
    flatDist,
    playerHeadPos,
    currentDimension = 'overworld',
    ignorePlayer = false
  ) {
    const cfg = mob.cfg;
    const attackScale = mob.attackScale || 1;
    const pos = mob.mesh.position;
    const inOverworld = currentDimension === 'overworld';
    const isDayOverworld = inOverworld && !isNight;

    mob.attackCooldown -= delta;
    mob.shootCooldown -= delta;
    mob.blinkCooldown -= delta;
    mob.aggroTime = Math.max(0, (mob.aggroTime || 0) - delta);
    const spiderDayNeutral = mob.type === 'spider' && isDayOverworld && mob.aggroTime <= 0;
    const endermanDayNeutral = mob.type === 'enderman' && isDayOverworld && mob.aggroTime <= 0;
    const neutralByDay = spiderDayNeutral || endermanDayNeutral;
    const ignorePlayerAggro = ignorePlayer || neutralByDay;

    if (!ignorePlayerAggro) {
      const targetAngle = Math.atan2(toPlayer.x, toPlayer.z);
      mob.mesh.rotation.y = targetAngle;
    }

    const burnsInSun = cfg.behavior === 'hostile' && mob.type !== 'spider';
    const burning = isDayOverworld && burnsInSun && isSunlit(pos);
    if (burning) {
      mob.burnTime += delta;
      while (mob.burnTime >= 0.75) {
        mob.burnTime -= 0.75;
        const result = damageMob(mob, 2, 'sunlight');
        if (result.killed) break;
      }
      if (!mobs.includes(mob)) return;
    } else {
      mob.burnTime = 0;
    }

    applyMobEmissive(mob, burning);

    mobAttackOrigin.set(pos.x, pos.y + 1.05, pos.z);
    const needsFrequentLos = mob.type === 'creeper' || mob.type === 'enderman';
    const attackWindow = Math.max(cfg.meleeRange || 1.35, cfg.rangedRange || 0) + 3.5;
    const shouldCheckLos = needsFrequentLos || flatDist <= attackWindow;
    const canSeePlayer = shouldCheckLos
      ? getHostileLineOfSightToPlayer(mob, mobAttackOrigin, playerHeadPos, flatDist, delta)
      : true;

    if (mob.type === 'enderman' && !ignorePlayerAggro) {
      const inWater = world.isWaterAt(Math.round(pos.x), Math.round(pos.y + 0.3), Math.round(pos.z));
      if (inWater) {
        const result = damageMob(mob, 2.5, 'water');
        if (result.killed) return;
        if (mob.blinkCooldown <= 0 || Math.random() < 0.28) {
          if (tryTeleportMobNear(mob, playerHeadPos, 8, 16, true, 24)) {
            mob.blinkCooldown = 1.5 + Math.random() * 2.1;
          }
        }
      } else if (mob.blinkCooldown <= 0 && canSeePlayer) {
        if (flatDist > 5.4 && flatDist < 24) {
          if (tryTeleportMobNear(mob, playerHeadPos, 2.2, 5.8, true, 18)) {
            mob.blinkCooldown = 2.0 + Math.random() * 2.2;
          }
        } else if (flatDist < 2.0) {
          if (tryTeleportMobNear(mob, playerHeadPos, 6.8, 13.5, true, 18)) {
            mob.blinkCooldown = 1.8 + Math.random() * 2.4;
          }
        }
      }
    }

    if (ignorePlayerAggro) {
      mob.aggroTime = 0;
      mob.wanderTime -= delta;
      if (mob.wanderTime <= 0 || mob.wanderDir.lengthSq() < 0.01) {
        const angle = Math.random() * Math.PI * 2;
        mob.wanderDir.set(Math.cos(angle), 0, Math.sin(angle));
        mob.wanderTime = 1.1 + Math.random() * 1.8;
      }
      const neutralFactor = mob.type === 'enderman' ? 0.5 : 0.42;
      const dayStep = cfg.speed * neutralFactor * delta;
      moveMobWithCollision(mob, mob.wanderDir.x * dayStep, mob.wanderDir.z * dayStep, delta, 10, false, flatDist);
      if (mob.wanderDir.lengthSq() > 0.001) {
        mob.mesh.rotation.y = Math.atan2(mob.wanderDir.x, mob.wanderDir.z);
      }
    } else {
      if (flatDist > 0.8) {
        const step = cfg.speed * delta;
        const dirX = toPlayer.x / (flatDist || 1);
        const dirZ = toPlayer.z / (flatDist || 1);
        const beforeX = pos.x;
        const beforeZ = pos.z;

        // stuck detection: try lateral nudge to navigate through doorways/openings
        if (mob.stuckAccum > 0.3) {
          // try perpendicular directions
          const perpLX = -dirZ * step;
          const perpLZ = dirX * step;
          const perpRX = dirZ * step;
          const perpRZ = -dirX * step;
          const cx = pos.x, cy = pos.y, cz = pos.z;
          const leftClear = !mobCollidesAt(cx + perpLX * 3, cy, cz + perpLZ * 3);
          const rightClear = !mobCollidesAt(cx + perpRX * 3, cy, cz + perpRZ * 3);
          if (leftClear && !rightClear) {
            moveMobWithCollision(mob, perpLX + dirX * step * 0.3, perpLZ + dirZ * step * 0.3, delta, 10, false, flatDist);
          } else if (rightClear && !leftClear) {
            moveMobWithCollision(mob, perpRX + dirX * step * 0.3, perpRZ + dirZ * step * 0.3, delta, 10, false, flatDist);
          } else if (leftClear && rightClear) {
            // pick random side
            const side = (mob.id % 2 === 0) ? 1 : -1;
            moveMobWithCollision(mob, side * perpLX + dirX * step * 0.3, side * perpLZ + dirZ * step * 0.3, delta, 10, false, flatDist);
          } else {
            moveMobWithCollision(mob, dirX * step, dirZ * step, delta, 10, false, flatDist);
          }
          mob.stuckAccum = Math.max(0, mob.stuckAccum - 0.4);
        } else {
          moveMobWithCollision(mob, dirX * step, dirZ * step, delta, 10, false, flatDist);
        }

        // check how far mob actually moved
        const movedDist = Math.sqrt((pos.x - beforeX) ** 2 + (pos.z - beforeZ) ** 2);
        const intendedDist = step;
        if (intendedDist > 0.001 && movedDist < intendedDist * 0.2) {
          mob.stuckAccum = Math.min(1.5, mob.stuckAccum + delta);
        } else {
          mob.stuckAccum = 0;
        }
      } else {
        moveMobWithCollision(mob, 0, 0, delta, 10, false, flatDist);
        mob.stuckAccum = 0;
      }
    }

    const canMelee = !ignorePlayerAggro;
    if (cfg.meleeDamage && canMelee && flatDist <= (cfg.meleeRange || 1.35) && mob.attackCooldown <= 0 && canSeePlayer) {
      emitMobSound(mob, 'attack', { mode: 'melee', distance: flatDist });
      onDamagePlayer(cfg.meleeDamage * attackScale, mob.type, mob.mesh.position);
      mob.attackCooldown = cfg.attackCd || 1.0;
    }

    if (!ignorePlayerAggro && cfg.rangedDamage && cfg.rangedRange && cfg.shootCd) {
      if (flatDist <= cfg.rangedRange && mob.shootCooldown <= 0 && canSeePlayer) {
        emitMobSound(mob, 'attack', { mode: 'ranged', distance: flatDist });
        rangedShotOrigin.set(pos.x, pos.y + 1.4, pos.z);
        spawnMobArrow(rangedShotOrigin, playerHeadPos, (cfg.rangedDamage || 6) * attackScale, mob.type);
        mob.shootCooldown = cfg.shootCd;
      }
    }

    if (mob.type === 'creeper') {
      const explodeDamage = (cfg.explodeDamage || 0) * attackScale;
      const explodeRange = (cfg.explodeRange || 0) + (mob.isElite ? 0.6 : 0);
      const creeperFuseVerticalGap = Math.abs(playerHeadPos.y - (pos.y + 1.0));
      const creeperFuseDist = Math.hypot(flatDist, creeperFuseVerticalGap);
      const canTriggerFuse = !ignorePlayerAggro && canSeePlayer && creeperFuseDist < 2.35 && creeperFuseVerticalGap <= 2.6;
      if (canTriggerFuse) {
        mob.fuse += delta;
        const fuseNorm = THREE.MathUtils.clamp(mob.fuse / Math.max(0.001, cfg.fuseTime || 1), 0, 1);
        const fuseAudioStep = Math.floor(fuseNorm * 9);
        if (fuseAudioStep > mob.lastFuseAudioStep) {
          emitMobSound(mob, 'creeper_fuse', {
            distance: creeperFuseDist,
            progress: fuseNorm,
            urgent: fuseNorm >= 0.72
          });
          mob.lastFuseAudioStep = fuseAudioStep;
        }
        const pulse = 1 + Math.sin(mob.fuse * 24) * 0.08;
        mob.mesh.scale.setScalar(pulse);
        if (mob.fuse >= cfg.fuseTime) {
          const blastPos = pos.clone();
          const blastStats = getPlayerBlastStats(blastPos);
          if (blastStats.distance < explodeRange && blastStats.exposure > 0) {
            const falloff = Math.max(0, 1 - blastStats.distance / explodeRange);
            const dealt = explodeDamage * falloff * blastStats.exposure;
            if (dealt > 0.2) {
              onDamagePlayer(Math.max(1, dealt), 'creeper_explode', blastPos);
            }
          }
          hurtMobsInRadius(blastPos, 3.2, 20);
          emitMobSound(mob, 'explode', { distance: creeperFuseDist });
          if (onCreeperExplode) onCreeperExplode(blastPos);
          spawnExplosionFx(blastPos);
          startMobDeath(mob, 'explode');
          return;
        }
      } else {
        mob.fuse = Math.max(0, mob.fuse - delta * 1.6);
        if (mob.fuse <= 0.03) mob.lastFuseAudioStep = -1;
        mob.mesh.scale.setScalar(1);
      }
    }
  }

  function getEndCenterX() {
    if (typeof world.toDimensionX === 'function') return world.toDimensionX(0, 'end');
    if (typeof world.getEndXOffset === 'function') return world.getEndXOffset();
    return 0;
  }

  function findEndDragon() {
    for (const mob of mobs) {
      if (mob.type === 'ender_dragon' && mob.dimension === 'end') return mob;
    }
    return null;
  }

  function ensureEndDragonSpawn(currentDimension, playerPos) {
    if (currentDimension !== 'end') return;
    if (endDragonDefeated) return;
    if (findEndDragon()) return;

    const centerX = getEndCenterX();
    const surfaceY = world.getSurfaceHeight(centerX, END_CENTER_Z);
    const spawnY = Math.max(surfaceY + DRAGON_MIN_ALTITUDE, playerPos.y + 6.5);
    dragonSpawnPos.set(centerX, spawnY, END_CENTER_Z);
    spawnMob('ender_dragon', dragonSpawnPos.x, dragonSpawnPos.y, dragonSpawnPos.z, { dimension: 'end' });
  }

  function updateDragonVisuals(mob) {
    const hurtPulse = mob.hurtFlash > 0;
    const aura = 0.09 + Math.sin(performance.now() * 0.006 + mob.id) * 0.045;
    const emissiveColor = hurtPulse ? 0xff2e2e : 0x45278a;
    const emissiveIntensity = hurtPulse ? 0.45 : aura;
    for (const mat of mob.mats) {
      mat.emissive.setHex(emissiveColor);
      mat.emissiveIntensity = emissiveIntensity;
    }

    if (!mob.parts) return;
    const flap = Math.sin(mob.bossTime * 7.3 + mob.id) * 0.62;
    if (mob.parts.wingL) {
      mob.parts.wingL.rotation.z = 0.32 + flap;
      mob.parts.wingL.rotation.y = -0.08;
    }
    if (mob.parts.wingR) {
      mob.parts.wingR.rotation.z = -0.32 - flap;
      mob.parts.wingR.rotation.y = 0.08;
    }
    if (mob.parts.head) {
      mob.parts.head.rotation.x = Math.sin(mob.bossTime * 2.0) * 0.08;
    }
  }

  function updateDragonBoss(mob, delta, flatDist, playerHeadPos) {
    const cfg = mob.cfg;
    const pos = mob.mesh.position;
    const centerX = getEndCenterX();
    const baseY = Math.max(world.getSurfaceHeight(centerX, END_CENTER_Z) + DRAGON_MIN_ALTITUDE, 10);

    mob.bossTime += delta;
    mob.attackCooldown -= delta;
    mob.swoopCooldown -= delta;

    dragonMoveDir.set(0, 0, 1);
    if (mob.swoopTimer > 0) {
      mob.swoopTimer = Math.max(0, mob.swoopTimer - delta);
      dragonTarget.set(playerHeadPos.x, Math.max(baseY - 4.5, playerHeadPos.y + 0.2), playerHeadPos.z);
      dragonMoveDir.copy(dragonTarget).sub(pos);
      const dist = dragonMoveDir.length();
      if (dist > 0.001) {
        dragonMoveDir.multiplyScalar(1 / dist);
        pos.addScaledVector(dragonMoveDir, Math.min(dist, cfg.speed * 1.28 * delta));
      }
      if (mob.swoopTimer <= 0 || dist < 3.2) {
        mob.swoopCooldown = 3.1 + Math.random() * 2.4;
      }
    } else {
      mob.orbitAngle += delta * (0.44 + Math.sin(mob.bossTime * 0.5 + mob.id) * 0.06);
      const orbitRadius = mob.orbitRadius + Math.sin(mob.bossTime * 0.33 + mob.id) * 2.4;
      dragonTarget.set(
        centerX + Math.cos(mob.orbitAngle) * orbitRadius,
        baseY + Math.sin(mob.bossTime * 1.08 + mob.id) * DRAGON_ORBIT_Y_SWAY,
        END_CENTER_Z + Math.sin(mob.orbitAngle) * orbitRadius
      );
      dragonMoveDir.copy(dragonTarget).sub(pos);
      const dist = dragonMoveDir.length();
      if (dist > 0.001) {
        dragonMoveDir.multiplyScalar(1 / dist);
        pos.addScaledVector(dragonMoveDir, Math.min(dist, cfg.speed * delta));
      }

      if (mob.swoopCooldown <= 0 && flatDist <= DRAGON_SWOOP_TRIGGER_DIST) {
        mob.swoopTimer = DRAGON_SWOOP_DURATION + Math.random() * 0.45;
      }
    }

    const moveLenSq = dragonMoveDir.lengthSq();
    if (moveLenSq > 0.0001) {
      mob.mesh.rotation.y = Math.atan2(dragonMoveDir.x, dragonMoveDir.z);
      mob.mesh.rotation.x = THREE.MathUtils.clamp(dragonMoveDir.y * -0.26, -0.32, 0.32);
    }
    const verticalGap = Math.abs(playerHeadPos.y - pos.y);
    if (cfg.meleeDamage && flatDist <= (cfg.meleeRange || 3.6) && verticalGap <= 4.8 && mob.attackCooldown <= 0) {
      const swipeDamage = cfg.meleeDamage * (mob.swoopTimer > 0 ? 1.2 : 1.0);
      emitMobSound(mob, 'attack', { mode: 'melee', distance: flatDist });
      onDamagePlayer(swipeDamage, mob.type, mob.mesh.position);
      mob.attackCooldown = cfg.attackCd || 1.1;
    }

    updateDragonVisuals(mob);
  }

  function getMobAiCadence(mob, flatDist) {
    if (!mob || mob.cfg?.behavior === 'boss') return 1;
    if (mob.hurtFlash > 0.01) return 1;
    if (mob.type === 'creeper' && mob.fuse > 0.02) return 1;

    if (flatDist <= MOB_AI_NEAR_DIST) return 1;
    if (flatDist <= MOB_AI_MID_DIST) return 2;
    if (flatDist <= MOB_AI_FAR_DIST) return 3;
    return 4;
  }

  function shouldShowMobHpRoot(mob, flatDist) {
    if (!mob) return false;
    if (mob.cfg?.behavior === 'boss') return true;
    if (mob.hurtFlash > 0.01) return true;
    if (mob.type === 'creeper' && mob.fuse > 0.02) return true;
    if ((mob.aggroTime || 0) > 0.3 && flatDist <= MOB_HP_SHOW_DISTANCE + 6) return true;
    if ((mob.loveTime || 0) > 0.01 && flatDist <= MOB_HP_SHOW_DISTANCE + 4) return true;
    return flatDist <= MOB_HP_SHOW_DISTANCE;
  }

  function shouldDespawnMob(mob, flatDist, verticalGap = 0) {
    if (!mob || mob.cfg?.behavior !== 'hostile') return false;
    if (mob.cfg?.behavior === 'boss') return false;
    if (mob.isElite) return false;
    if ((mob.spawnGrace || 0) > 0) return false;
    if (mob.type === 'creeper' && mob.fuse > 0.02) return false;
    if ((mob.aggroTime || 0) > 0.4) return false;
    if (mob.hurtFlash > 0.01) return false;

    const dist = Math.max(0, Number(flatDist) || 0);
    const vGap = Math.max(0, Number(verticalGap) || 0);
    if (dist >= MOB_HARD_DESPAWN_DISTANCE) return true;
    return dist >= MOB_SOFT_DESPAWN_DISTANCE && vGap >= 18;
  }

  function shouldRenderMobVisual(mob, playerPos, flatDist, nowSeconds) {
    if (!mob) return false;
    if (mob.cfg?.behavior === 'boss') {
      mob.visualRetainUntil = nowSeconds + MOB_VISUAL_RETAIN_SECONDS;
      return true;
    }
    if (flatDist <= MOB_VISUAL_ALWAYS_DISTANCE) {
      mob.visualRetainUntil = nowSeconds + MOB_VISUAL_RETAIN_SECONDS;
      return true;
    }
    if (flatDist > MOB_VISUAL_RENDER_DISTANCE) return false;
    if (!camera) {
      mob.visualRetainUntil = nowSeconds + MOB_VISUAL_RETAIN_SECONDS;
      return true;
    }

    camera.getWorldDirection(mobViewDir);
    const dirLen = Math.hypot(mobViewDir.x, mobViewDir.z);
    if (dirLen <= 0.0001 || flatDist <= 0.0001) {
      mob.visualRetainUntil = nowSeconds + MOB_VISUAL_RETAIN_SECONDS;
      return true;
    }

    const toMobX = mob.mesh.position.x - playerPos.x;
    const toMobZ = mob.mesh.position.z - playerPos.z;
    const forwardX = mobViewDir.x / dirLen;
    const forwardZ = mobViewDir.z / dirLen;
    const alignment = ((toMobX * forwardX) + (toMobZ * forwardZ)) / flatDist;
    const distRatio = THREE.MathUtils.clamp(
      (flatDist - MOB_VISUAL_ALWAYS_DISTANCE) / Math.max(1, MOB_VISUAL_RENDER_DISTANCE - MOB_VISUAL_ALWAYS_DISTANCE),
      0,
      1
    );
    const minDot = THREE.MathUtils.lerp(-0.58, 0.12, distRatio);
    const hysteresis = mob.mesh.visible ? MOB_VISUAL_HYSTERESIS_DOT : 0;
    if (alignment >= (minDot - hysteresis)) {
      mob.visualRetainUntil = nowSeconds + MOB_VISUAL_RETAIN_SECONDS;
      return true;
    }

    return (Number(mob.visualRetainUntil) || 0) > nowSeconds;
  }

  function updateMobs(delta, isNight, nightStrength, currentDimension = 'overworld', dayCount = 1) {
    ecosystemQueryCache.clear();
    lightQueryCache.clear();
    mobUpdateFrame = (mobUpdateFrame + 1) % 1048576;
    runtimeTerrainProbeBudget = runtimePerformanceTier <= 0
      ? 18
      : (runtimePerformanceTier === 1 ? 34 : 68);
    runtimeHighPrecisionMoveBudget = runtimePerformanceTier <= 0
      ? 10
      : (runtimePerformanceTier === 1 ? 18 : 32);
    const safeDelta = Math.max(0, Number(delta) || 0);
    let losBudgetBase = runtimePerformanceTier <= 0
      ? 14
      : (runtimePerformanceTier === 1 ? 24 : 42);
    if (safeDelta > 0.024) losBudgetBase = Math.max(8, Math.floor(losBudgetBase * 0.76));
    if (mobs.length > 28) losBudgetBase = Math.max(8, Math.floor(losBudgetBase * 0.82));
    runtimeLosRefreshBudgetInitial = losBudgetBase;
    runtimeLosRefreshBudget = losBudgetBase;
    runtimeLosRefreshUsed = 0;
    runtimeLosRefreshSkipped = 0;
    const playerPos = player.getPosition();
    const ignoreHostiles = creativeModeActive;
    const nowSeconds = performance.now() * 0.001;
    playerHead.set(playerPos.x, playerPos.y - 0.45, playerPos.z);
    ensureEndDragonSpawn(currentDimension, playerPos);

    for (let i = mobs.length - 1; i >= 0; i--) {
      const mob = mobs[i];
      const pos = mob.mesh.position;
      const active = mob.dimension === currentDimension;
      if (!active) {
        mob.mesh.visible = false;
        mob.aiAccum = 0;
        mob.hpRoot.visible = false;
        continue;
      }
      tempVec.copy(playerPos).sub(pos);
      const flatDist = Math.hypot(tempVec.x, tempVec.z);
      const verticalGap = Math.abs(tempVec.y);
      const visualVisible = shouldRenderMobVisual(mob, playerPos, flatDist, nowSeconds);
      mob.mesh.visible = visualVisible;
      mob.spawnGrace = Math.max(0, (mob.spawnGrace || 0) - delta);

      if (shouldDespawnMob(mob, flatDist, verticalGap)) {
        scene.remove(mob.mesh);
        hitboxToMob.delete(mob.hitbox.uuid);
        mobs.splice(i, 1);
        continue;
      }

      const avoidWater = mob.cfg?.spawnInWater !== true;
      const terrainCadenceBias = runtimePerformanceTier <= 0 ? 1 : 0;
      const terrainProbeCadence = (flatDist <= MOB_AI_NEAR_DIST ? 1 : (flatDist <= MOB_AI_MID_DIST ? 2 : 4)) + terrainCadenceBias;
      const shouldProbeTerrain = terrainProbeCadence <= 1
        || ((mob.id + mobUpdateFrame) % terrainProbeCadence === 0);
      const allowTerrainProbe = runtimePerformanceTier >= 2 || runtimeTerrainProbeBudget > 0;
      if (mob.cfg?.behavior !== 'boss' && shouldProbeTerrain && allowTerrainProbe) {
        if (runtimePerformanceTier < 2) {
          runtimeTerrainProbeBudget = Math.max(0, runtimeTerrainProbeBudget - 1);
        }
        if (!mobCollidesAt(pos.x, pos.y, pos.z)) {
          // No rescue work needed when not intersecting terrain.
        } else {
          const rescued = tryResolveMobInsideTerrain(mob, avoidWater)
            || tryTeleportMobNear(mob, playerPos, 6, 14, avoidWater, 20);
          if (!rescued) continue;
        }
      }

      if (mob.cfg.behavior === 'passive') {
        mob.loveTime = Math.max(0, (mob.loveTime || 0) - delta);
        mob.breedCooldown = Math.max(0, (mob.breedCooldown || 0) - delta);
        if (mob.isBaby) {
          mob.babyGrowTime = Math.max(0, (mob.babyGrowTime || 0) - delta);
          if (mob.babyGrowTime <= 0) {
            mob.isBaby = false;
            mob.mesh.scale.setScalar(1);
            mob.maxHp = mob.cfg.hp;
            mob.hp = Math.min(mob.hp, mob.maxHp);
            refreshMobHpBar(mob);
          }
        } else {
          mob.babyGrowTime = 0;
        }
      }

      if (isMobInLava(mob)) {
        mob.lavaTick = (mob.lavaTick || 0) + delta;
        while (mob.lavaTick >= MOB_LAVA_DAMAGE_INTERVAL) {
          mob.lavaTick -= MOB_LAVA_DAMAGE_INTERVAL;
          const result = damageMob(mob, MOB_LAVA_DAMAGE, 'lava');
          if (result.killed) break;
        }
        if (!mobs.includes(mob)) continue;
      } else {
        mob.lavaTick = 0;
      }

      const showHpRoot = visualVisible && shouldShowMobHpRoot(mob, flatDist);
      mob.hpRoot.visible = showHpRoot;
      if (mob.nameTag) mob.nameTag.visible = visualVisible && flatDist <= MOB_NAME_SHOW_DISTANCE;

      mob.voiceCooldown = (mob.voiceCooldown ?? getAmbientCooldown(mob.type, mob.cfg?.behavior)) - delta;
      const hearRange = mob.cfg?.behavior === 'hostile' ? 26 : 16;
      const creeperIsFusing = mob.type === 'creeper' && mob.fuse > 0.04;
      if (!creeperIsFusing && flatDist <= hearRange && mob.voiceCooldown <= 0) {
        emitMobSound(mob, 'ambient', { distance: flatDist });
        mob.voiceCooldown = getAmbientCooldown(mob.type, mob.cfg?.behavior);
      }

      mob.hurtFlash = Math.max(0, mob.hurtFlash - delta);
      mob.aiAccum = Math.min(MOB_AI_ACCUM_MAX, (mob.aiAccum || 0) + delta);
      let cadence = getMobAiCadence(mob, flatDist);
      if (!visualVisible && mob.cfg?.behavior !== 'boss' && flatDist > MOB_VISUAL_ALWAYS_DISTANCE) {
        cadence += flatDist > MOB_AI_FAR_DIST ? 2 : 1;
      }
      if (mobs.length >= MOB_HEAVY_LOAD_COUNT && mob.cfg?.behavior !== 'boss' && flatDist > MOB_AI_MID_DIST) {
        cadence += 1;
      }
      if (mobs.length >= MOB_CRITICAL_LOAD_COUNT && mob.cfg?.behavior !== 'boss' && flatDist > MOB_AI_FAR_DIST) {
        cadence += 2;
      }
      if (runtimePerformanceTier <= 0 && mob.cfg?.behavior !== 'boss' && !mob.isElite) {
        cadence += flatDist <= MOB_AI_NEAR_DIST ? 1 : 2;
      }
      const shouldRunAi = cadence <= 1 || ((mob.id + mobUpdateFrame) % cadence === 0);

      if (shouldRunAi) {
        const simDelta = Math.max(0.008, mob.aiAccum || delta);
        mob.aiAccum = 0;

        if (mob.cfg.behavior === 'passive') {
          applyMobEmissive(mob, false);
          updatePassiveMob(mob, simDelta, tempVec, flatDist);
        } else if (mob.cfg.behavior === 'boss') {
          updateDragonBoss(mob, simDelta, flatDist, playerHead);
        } else {
          updateHostileMob(mob, simDelta, isNight, tempVec, flatDist, playerHead, currentDimension, ignoreHostiles);
          if (!mobs.includes(mob)) continue;
        }
      }

      if (showHpRoot) {
        mob.hpRoot.lookAt(playerHead.x, pos.y + mob.hpRoot.position.y, playerHead.z);
      }
    }

    if (currentDimension === 'overworld') {
      const ecosystem = getEcosystemAt(playerPos.x, playerPos.z);
      const playerPoi = ecosystem?.poi || getPoiAt(playerPos.x, playerPos.z);
      const nearThreatPoi = isThreatPoi(playerPoi);
      const nearShipwreck = !!ecosystem?.shipwreck;
      const biome = ecosystem?.biome || null;
      const coastalBiome = biome === 'ocean' || biome === 'wetland';
      const newbieSpawnProtection = dayCount <= NEWBIE_WORLD_SPAWN_GRACE_DAYS
        && isWithinWorldSpawnSafeZone(playerPos.x, playerPos.z);
      const effectiveNearThreatPoi = nearThreatPoi && !newbieSpawnProtection;
      const effectiveNearShipwreck = nearShipwreck && !newbieSpawnProtection;

      hostileSpawnTimer += delta;
      const hostileDayTuning = getOverworldHostileDayTuning(dayCount);
      let hostileEntries = applyHostileDayScaling(HOSTILE_SPAWN_WEIGHTS, dayCount, 'overworld');
      let hostileMinDist = 16;
      let hostileMaxDist = 32;
      let hostileInterval = HOSTILE_SPAWN_BASE_INTERVAL - nightStrength * HOSTILE_SPAWN_NIGHT_ACCEL;
      let preferPoiType = null;
      let preferShipwreck = false;
      let hostileSpawnAttempts = 7;

      if (!newbieSpawnProtection && playerPoi?.type === 'raider_outpost') {
        hostileEntries = applyHostileDayScaling(OUTPOST_HOSTILE_SPAWN_WEIGHTS, dayCount, 'overworld');
        hostileMinDist = 8;
        hostileMaxDist = 22;
        hostileInterval = Math.max(1.7, hostileInterval - 0.9);
        preferPoiType = 'raider_outpost';
      } else if (!newbieSpawnProtection && playerPoi?.type === 'monster_den') {
        hostileEntries = applyHostileDayScaling(MONSTER_DEN_HOSTILE_SPAWN_WEIGHTS, dayCount, 'overworld');
        hostileMinDist = 7;
        hostileMaxDist = 21;
        hostileInterval = Math.max(1.55, hostileInterval - 1.05);
        preferPoiType = 'monster_den';
      } else if (!newbieSpawnProtection && playerPoi?.type === 'ancient_ruin') {
        hostileEntries = applyHostileDayScaling(RUIN_HOSTILE_SPAWN_WEIGHTS, dayCount, 'overworld');
        hostileMinDist = 10;
        hostileMaxDist = 25;
        hostileInterval = Math.max(2.0, hostileInterval - 0.45);
        preferPoiType = 'ancient_ruin';
      } else if (!newbieSpawnProtection && nearShipwreck) {
        hostileEntries = applyHostileDayScaling(SHIPWRECK_HOSTILE_SPAWN_WEIGHTS, dayCount, 'overworld');
        hostileMinDist = 9;
        hostileMaxDist = 24;
        hostileInterval = Math.max(1.9, hostileInterval - 0.55);
        preferShipwreck = true;
      }

      hostileInterval = Math.max(1.35, hostileInterval + hostileDayTuning.intervalOffset);
      if (coastalBiome) {
        hostileInterval += nearShipwreck ? 0.28 : 0.52;
        hostileSpawnAttempts = nearShipwreck ? 6 : 5;
      }
      if (newbieSpawnProtection) {
        hostileMinDist = Math.max(hostileMinDist, 24);
        hostileMaxDist = Math.max(hostileMaxDist, 40);
        hostileInterval += dayCount <= 1 ? 3.0 : 2.2;
      }
      let hostileCap = Math.max(
        3,
        MAX_HOSTILE_MOBS + (effectiveNearThreatPoi ? 2 : 0) + (effectiveNearShipwreck ? 1 : 0) + hostileDayTuning.capOffset
      );
      if (coastalBiome) {
        hostileCap = Math.max(2, hostileCap - (nearShipwreck ? 1 : 2));
      }
      if (newbieSpawnProtection) {
        const newbieCap = dayCount <= 1 ? 1 : 2;
        hostileCap = Math.min(hostileCap, newbieCap);
      }
      if (hostileSpawnTimer > hostileInterval && countBehavior('hostile', 'overworld') < hostileCap) {
        hostileSpawnTimer = 0;
        trySpawnNearPlayer(
          playerPos,
          hostileEntries,
          hostileMinDist,
          hostileMaxDist,
          null,
          'overworld',
          (candidate) => {
            if (newbieSpawnProtection && isWithinWorldSpawnSafeZone(candidate.x, candidate.z)) return false;
            const candidatePoi = getPoiAt(candidate.x, candidate.z);
            const candidateThreatPoi = isThreatPoi(candidatePoi);
            const candidateEcosystem = getEcosystemAt(candidate.x, candidate.z);
            const candidateShipwreck = !!candidateEcosystem?.shipwreck;
            const allowDayThreatSpawn = candidateThreatPoi || candidateShipwreck;

            if (!allowDayThreatSpawn && !(isNight || !isSunlit(candidate))) return false;
            if (hasNearbyLightSource(candidate.x, candidate.y, candidate.z, 5)) return false;

            if (preferPoiType && (!candidatePoi || candidatePoi.type !== preferPoiType) && Math.random() < 0.58) {
              return false;
            }
            if (preferShipwreck && !candidateShipwreck && Math.random() < 0.55) {
              return false;
            }
            return true;
          },
          hostileSpawnAttempts
        );
      }

      passiveSpawnTimer += delta;
      if (!isNight) {
        let passiveEntries = PASSIVE_SPAWN_WEIGHTS;
        let passiveMinDist = 10;
        let passiveMaxDist = 26;
        let passiveCap = 14;
        let preferFriendlyPoiType = null;
        let preferWetBiome = false;
        let preferMountainBiome = false;

        if (playerPoi?.type === 'hamlet') {
          passiveEntries = HAMLET_PASSIVE_SPAWN_WEIGHTS;
          passiveMinDist = 8;
          passiveMaxDist = 22;
          passiveCap = 18;
          preferFriendlyPoiType = 'hamlet';
        } else if (playerPoi?.type === 'hospital') {
          passiveEntries = HAMLET_PASSIVE_SPAWN_WEIGHTS;
          passiveMinDist = 8;
          passiveMaxDist = 22;
          passiveCap = 16;
          preferFriendlyPoiType = 'hospital';
        } else if (playerPoi?.type === 'tribe_camp') {
          passiveEntries = TRIBE_PASSIVE_SPAWN_WEIGHTS;
          passiveMinDist = 8;
          passiveMaxDist = 23;
          passiveCap = 17;
          preferFriendlyPoiType = 'tribe_camp';
        } else if (nearShipwreck || biome === 'wetland') {
          passiveEntries = WETLAND_PASSIVE_SPAWN_WEIGHTS;
          passiveMinDist = 9;
          passiveMaxDist = 24;
          passiveCap = 14;
          preferWetBiome = true;
        } else if (biome === 'mountain') {
          passiveEntries = MOUNTAIN_PASSIVE_SPAWN_WEIGHTS;
          passiveMinDist = 10;
          passiveMaxDist = 25;
          passiveCap = 15;
          preferMountainBiome = true;
        }

        if (passiveSpawnTimer > 3.4 && countBehavior('passive', 'overworld') < passiveCap) {
          passiveSpawnTimer = 0;
          trySpawnNearPlayer(
            playerPos,
            passiveEntries,
            passiveMinDist,
            passiveMaxDist,
            null,
            'overworld',
            (candidate) => {
              const candidatePoi = getPoiAt(candidate.x, candidate.z);
              const candidateEcosystem = getEcosystemAt(candidate.x, candidate.z);
              if (preferFriendlyPoiType) {
                if (candidatePoi && candidatePoi.type === preferFriendlyPoiType) return true;
                return Math.random() > 0.7;
              }
              if (preferWetBiome) {
                if (candidateEcosystem?.shipwreck) return true;
                if (candidateEcosystem?.biome === 'wetland') return true;
                return Math.random() > 0.75;
              }
              if (preferMountainBiome) {
                if (candidateEcosystem?.biome === 'mountain') return true;
                return Math.random() > 0.75;
              }
              return true;
            },
            7
          );
        }
      }
      return;
    }

    passiveSpawnTimer = 0;
    hostileSpawnTimer += delta;

    if (currentDimension === 'nether') {
      if (hostileSpawnTimer > NETHER_HOSTILE_SPAWN_INTERVAL && countBehavior('hostile', 'nether') < MAX_NETHER_HOSTILES) {
        hostileSpawnTimer = 0;
        const netherEntries = applyProgressionSpawnGates(NETHER_HOSTILE_SPAWN_WEIGHTS, dayCount, 'nether');
        trySpawnNearPlayer(playerPos, netherEntries, 12, 28, null, 'nether', null, 6);
      }
      return;
    }

    if (currentDimension === 'end') {
      if (hostileSpawnTimer > END_HOSTILE_SPAWN_INTERVAL && countBehavior('hostile', 'end') < MAX_END_HOSTILES) {
        hostileSpawnTimer = 0;
        trySpawnNearPlayer(playerPos, END_HOSTILE_SPAWN_WEIGHTS, 14, 30, null, 'end', null, 6);
      }
      return;
    }

    hostileSpawnTimer = 0;
  }

  function updateArrows(delta, currentDimension = 'overworld') {
    const p = player.getPosition();
    for (let i = arrows.length - 1; i >= 0; i--) {
      const arrow = arrows[i];
      arrow.ttl -= delta;
      if (arrow.ttl <= 0) {
        scene.remove(arrow.mesh);
        arrows.splice(i, 1);
        continue;
      }

      if (arrow.dimension !== currentDimension) {
        arrow.mesh.visible = false;
        continue;
      }
      arrow.mesh.visible = true;

      arrow.vel.y -= 5.6 * delta;
      arrow.mesh.position.addScaledVector(arrow.vel, delta);
      arrowLookTarget.copy(arrow.mesh.position).add(arrow.vel);
      arrow.mesh.lookAt(arrowLookTarget);

      const bx = Math.round(arrow.mesh.position.x);
      const by = Math.round(arrow.mesh.position.y);
      const bz = Math.round(arrow.mesh.position.z);
      if (world.isSolidAt(bx, by, bz)) {
        if (arrow.owner === 'player' && Math.random() < 0.6) {
          world.spawnDrop(arrow.mesh.position.x, arrow.mesh.position.y, arrow.mesh.position.z, 'arrow', {
            kind: 'item',
            color: 0xc8bfaf
          });
        }
        scene.remove(arrow.mesh);
        arrows.splice(i, 1);
        continue;
      }

      if (arrow.owner === 'mob') {
        if (creativeModeActive) continue;
        if (arrow.mesh.position.distanceToSquared(p) < 0.75) {
          onDamagePlayer(arrow.damage || 6, arrow.sourceType || 'skeleton_archer', arrow.mesh.position);
          scene.remove(arrow.mesh);
          arrows.splice(i, 1);
        }
        continue;
      }

      let hitMob = null;
      for (let m = 0; m < mobs.length; m++) {
        const mob = mobs[m];
        if (mob.dimension !== arrow.dimension) continue;
        const cx = mob.mesh.position.x;
        const cy = mob.mesh.position.y + (mob.cfg.behavior === 'boss' ? 1.9 : 0.9);
        const cz = mob.mesh.position.z;
        const d2 = (
          (arrow.mesh.position.x - cx) * (arrow.mesh.position.x - cx) +
          (arrow.mesh.position.y - cy) * (arrow.mesh.position.y - cy) +
          (arrow.mesh.position.z - cz) * (arrow.mesh.position.z - cz)
        );
        const hitRadiusSq = mob.cfg.behavior === 'boss' ? 8.8 : 0.66;
        if (d2 < hitRadiusSq) {
          hitMob = mob;
          break;
        }
      }

      if (hitMob) {
        if (hitMob.type === 'enderman' && Math.random() < 0.68) {
          if (tryTeleportMobNear(hitMob, p, 7, 15, true, 20)) {
            hitMob.aggroTime = Math.max(hitMob.aggroTime || 0, 18.0);
            hitMob.blinkCooldown = 1.9 + Math.random() * 2.1;
            scene.remove(arrow.mesh);
            arrows.splice(i, 1);
            continue;
          }
        }

        damageMob(hitMob, arrow.damage || 8, 'player');
        if (hitMob.cfg.behavior === 'passive') {
          hitMob.fleeTime = Math.max(hitMob.fleeTime, 2.2);
        }
        scene.remove(arrow.mesh);
        arrows.splice(i, 1);
      }
    }
  }

  function updateDeadMobs(delta) {
    for (let i = deadMobs.length - 1; i >= 0; i--) {
      const dead = deadMobs[i];
      dead.ttl -= delta;
      const t = 1 - Math.max(0, dead.ttl) / dead.duration;

      dead.mesh.rotation.x = -t * 1.45;
      dead.mesh.rotation.z += dead.spin * delta;
      dead.mesh.position.y = dead.baseY - Math.min(0.48, t * 0.5);

      const fade = 1 - Math.max(0, t - 0.35) / 0.65;
      for (const mat of dead.mats) mat.opacity = Math.max(0, fade);

      if (dead.ttl <= 0) {
        scene.remove(dead.mesh);
        deadMobs.splice(i, 1);
      }
    }
  }

  function updateEffects(delta) {
    for (let i = explosionFx.length - 1; i >= 0; i--) {
      const fx = explosionFx[i];
      fx.ttl -= delta;
      if (fx.ttl <= 0) {
        scene.remove(fx.mesh);
        explosionFx.splice(i, 1);
        continue;
      }

      const t = 1 - fx.ttl / 0.35;
      const scale = 0.6 + t * 4.2;
      fx.mesh.scale.set(scale, scale, scale);
      fx.mesh.material.opacity = 0.76 * (1 - t);
    }
  }

  function update(delta, isNight, nightStrength, currentDimension = 'overworld', dayCount = 1, opts = {}) {
    const tierRaw = Number(opts?.performanceTier);
    runtimePerformanceTier = Number.isFinite(tierRaw)
      ? Math.max(0, Math.min(2, Math.floor(tierRaw)))
      : 1;
    updateMobs(delta, isNight, nightStrength, currentDimension, dayCount);
    updateArrows(delta, currentDimension);
    updateDeadMobs(delta);
    updateEffects(delta);
  }

  return {
    update,
    clearAll,
    setCreativeMode(enabled) {
      creativeModeActive = !!enabled;
      if (!creativeModeActive) return;

      for (const mob of mobs) {
        if (mob.cfg?.behavior !== 'hostile') continue;
        mob.aggroTime = 0;
        mob.fuse = 0;
        mob.lastFuseAudioStep = -1;
        mob.mesh.scale.setScalar(1);
      }
    },
    refreshLocale() {
      for (const mob of mobs) {
        refreshMobDisplayName(mob);
      }
    },
    tryPlayerAttack,
    tryInteractVillager,
    tryFeedPassiveMob,
    spawnPlayerArrow,
    getMobCount() {
      return mobs.length;
    },
    getPerformanceSnapshot() {
      return {
        activeMobs: mobs.length,
        arrows: arrows.length,
        deadMobs: deadMobs.length,
        perfTier: runtimePerformanceTier,
        losBudget: runtimeLosRefreshBudgetInitial,
        losBudgetLeft: runtimeLosRefreshBudget,
        losUsed: runtimeLosRefreshUsed,
        losSkipped: runtimeLosRefreshSkipped,
        terrainProbeBudgetLeft: runtimeTerrainProbeBudget,
        precisionMoveBudgetLeft: runtimeHighPrecisionMoveBudget
      };
    }
  };
}
